import { ARTICLE_ROUTES, PREVIEW_ARTICLE_ROUTES } from './features/write/article-routes';
import { SPOTLIGHT_PLACES_DATA } from './features/spotlight/places/spotlight-places-data';
import { SpotlightPlacesHubPreviewData } from './features/spotlight/places/spotlight-places-fixture';
import { provideSpotlightFixtureSources } from './features/spotlight/data/spotlight-fixture-sources';
import { Router, Routes } from '@angular/router';
import { inject } from '@angular/core';
import { isTauri } from './core/tauri-runtime';
import {
  SV3_DATA_SOURCE,
  SV3_SHELL_DIALOGS,
} from './features/schedule-v3/services/sv3-data-source';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { adminRedirectGuard } from './core/guards/admin-redirect.guard';
import { previewOnlyGuard } from './core/guards/dev-only.guard';
import { RETIRED_HOME_ROUTES, toArticle, toWriter } from './core/guards/retired-home-routes';
import { toManageCateringApprovals } from './core/guards/manage-redirect';
import { RETIRED_GALLERY_PATHS, toPaperDocs } from './core/guards/paper-docs-redirect';
import { HOME_SERVICES_PATH, redirectKeepingQuery, toCateringOrder, toCateringOrders } from './features/home-workspace-preview/home-views';
import { simulatedAskProviders } from './shared/ai-widgets/ask-stream-simulator';
import { askPreviewProviders } from './features/ai-chat/ask-preview.providers';
import { askMeetingPreviewProviders } from './features/ai-chat/ask-meeting-preview.providers';
import { publicProfilePreviewProviders } from './features/people/public-profile-preview';
import { askMapPreviewProviders } from './features/ai-chat/ask-map-preview.providers';
import { askWeekPreviewProviders } from './features/ai-chat/ask-week-preview.providers';
import { vendorGuard } from './core/guards/vendor.guard';
import { anyActionGuard } from './core/guards/action.guard';
import { spaceDetailCanDeactivateGuard } from './core/guards/space-detail-can-deactivate.guard';
import { flowAccessGuard, flowBoardCodeGuard, flowOffPageGuard } from './features/flow-v2/access/flow-access.guards';
import { FLOW_V2_ROUTES } from './features/flow-v2/flow.routes';
import {
  LEGACY_SCHEDULE_PARAMS,
  scheduleLegacyRedirectGuard,
} from './core/guards/schedule-legacy-redirect.guard';
import {
  featureFlagAnyGuard,
  featureFlagGuard,
  redirectWhenFlagOn,
  trialFlagGuard,
} from './core/guards/feature-flag.guard';
import {
  CONTENT_STUDIO_WORKSPACE_FLAG,
  WORLD_WORKSPACE_FLAG,
} from './core/constants/navigation';
import {
  GamesApi,
  GamesGraphqlApi,
} from './features/games/data/games-api.service';
// No `GamesMockApi` here: `/preview/discover` names it in its own lazily
// loaded routes file, so the mock rooms hub stays out of the initial bundle.
import { DailyGameStore } from './features/games/data/daily-game.store';
// Route-level transport + clock override for `/preview/broadcast-publisher`.
// Static for the same reason as the array above — and imported from a
// `.providers.ts` that deliberately pulls in NO components, which is what
// actually keeps the harness component lazy. This line used to read
// `...preview.component`, and the claim beside it that "the component stays
// lazy" was false: the static edge reached the component, the component's
// `imports` reached BroadcastPublisherPageComponent, and 87,742 bytes of real
// publisher board landed in the initial bundle. Measured, then fixed; see the
// header of the providers file.
import { BROADCAST_PUBLISHER_PREVIEW_PROVIDERS } from './features/settings/pages/broadcast/broadcast-publisher-preview.providers';
// Route-level overrides for `/preview/front-page-studio` — the board harness's
// array plus one no-op, in a `.providers.ts` that imports NO components for
// exactly the reason recorded above this line.
import { FRONT_PAGE_STUDIO_PREVIEW_PROVIDERS } from './features/content/front-page/front-page-studio-preview.providers';
// Route-level CLOCK override for `/preview/front-page` — the same
// `BROADCAST_CLOCK` seam, so the harness and the band read one instant.
import { BROADCAST_FRONT_PAGE_PREVIEW_PROVIDERS } from './features/discover/bands/broadcast-front-page-preview.component';
// Route-level transport + identity override for `/preview/reader-analytics`.
// Same shape as the notifications harness above: real component, real service,
// substituted wire — see the component's docblock for why the live route
// cannot be reviewed.
import { READER_ANALYTICS_PREVIEW_PROVIDERS } from './features/reader/analytics/reader-analytics-preview.component';
import { BROADCAST_ACKNOWLEDGE_PREVIEW_PROVIDER } from './features/discover/data/broadcast-acknowledge-preview.service';
import { warmChunk } from './core/guards/warm-chunk.guard';
import { HUB_FEED_PREVIEW_PROVIDER } from './features/hubs/data/hub-feed-preview.service';
import { HUB_INVITES_PREVIEW_PROVIDER } from './features/hubs/data/hub-invites-preview.service';
import { HUB_CARD_PREVIEW_PROVIDER } from './features/hubs/data/hub-card-preview.service';
import { hubJoinGuard } from './core/guards/hub-join.guard';
import { HUB_PAGE_PREVIEW_PROVIDER } from './features/hubs/data/hub-page-preview.service';
import { CONTENT_ENGAGEMENT_PREVIEW_PROVIDER } from './shared/content/content-engagement-preview.service';
import { provideHubsCommentsPreview } from './shared/content/content-comments-preview.service';
import { HUB_DIRECTORY_PREVIEW_PROVIDER } from './features/hubs/data/hub-directory-preview.service';
import {
  HUB_CREATE_PREVIEW_PROVIDERS,
  HubInterestsPreviewService,
  HubPeoplePreviewService,
} from './features/hubs/create/hub-create-preview.service';
import { HubPeopleService } from './features/hubs/create/hub-people.service';
import { HubInterestsService } from './features/hubs/create/hub-interests.service';
import { HUB_SAVED_PREVIEW_PROVIDER } from './features/hubs/saved/hub-saved-preview.service';
import { HUB_EXPLORE_PREVIEW_PROVIDER } from './features/hubs/data/hub-explore-preview.service';
import { EDITORS_PICKS_PREVIEW_PROVIDER } from './features/hubs/data/editors-picks-preview.service';
import { HUB_COMPOSER_PREVIEW_PROVIDER } from './features/hubs/data/hub-composer-preview.service';
import { HUBS_PREVIEW_VIEWER_PROVIDER } from './features/hubs/data/hubs-preview-viewer';
import { COMMS_BAND_PREVIEW_PROVIDER } from './features/hubs/data/comms-band-preview.service';
import { HOME_TOP_PREVIEW_PROVIDER } from './features/hubs/data/home-top-preview.service';
import { HUBS_RAIL_PREVIEW_PROVIDER } from './features/hubs/data/hubs-rail-preview.service';
import { PLAN_VISIT_PREVIEW_PROVIDER } from './features/hubs/coming-in/plan-visit-preview.service';
import { HUBS_HEADS_UP_PREVIEW_PROVIDER } from './features/hubs/data/hubs-heads-up-preview.service';
import { HUB_EVENTS_PREVIEW_PROVIDER } from './features/hubs/events/hub-events-preview.service';
import { ORG_EVENTS_PREVIEW_PROVIDER } from './features/events/org-events-preview.service';
import { HUB_CHALLENGES_PREVIEW_PROVIDER } from './features/hubs/challenges/hub-challenges-preview.service';
import { HUB_INSIGHTS_PREVIEW_PROVIDER } from './features/hubs/data/hub-insights-preview.service';
import { HUBS_PREVIEW_FLAGS } from './features/hubs/hub-preview-flags';
import { HUB_PLACE_PREVIEW_PROVIDER } from './features/hubs/data/hub-place-preview.service';
import { HUB_READ_STATE_PREVIEW_PROVIDER } from './features/hubs/data/hub-read-state.service';
import { BROADCAST_EXTERNAL_CLICK_PREVIEW_PROVIDER } from './features/discover/data/broadcast-external-click-preview.service';
import { OrganisationVideosService } from './features/videos/organisation-videos.service';
// Static, not lazy: `resolve` takes a ResolveFn, not a loader. It costs the
// initial bundle nothing — the only thing it pulls in is CommunitiesService,
// which the app shell already injects for the community nav context.
import { communityContextResolver } from './features/communities/community-context.resolver';
// Retired /profile paths → where they went. Imported from a leaf file with no
// imports of its own, so the routing table and the Settings rail agree on the
// list without dragging the page model into the initial bundle.
import { RETIRED_ROUTE_REDIRECTS } from './features/profile/settings/settings-retired-routes';

/**
 * The Settings pages that are drawn from `settings-model.ts` rather than by
 * hand. All twelve load ONE component; the route's `settingsRoute` datum is
 * what tells it which page to render, and `''` is the overview.
 *
 * Adding a page here without adding it to the model gives a blank overview,
 * which is the failure the `settings-model.spec` route-coverage test catches.
 */
const GENERIC_SETTINGS_ROUTES: readonly { path: string; title: string }[] = [
  { path: '', title: 'Settings' },
  { path: 'account', title: 'Name and contact' },
  { path: 'regional', title: 'Language and region' },
  { path: 'dietary', title: 'Dietary requirements' },
  { path: 'site', title: 'Home site and desks' },
  { path: 'pattern', title: 'Working pattern' },
  { path: 'travel', title: 'Travel and commute' },
  { path: 'appearance', title: 'Appearance and map' },
  { path: 'meetings', title: 'Meetings' },
  { path: 'privacy', title: 'Who can see you' },
  { path: 'data', title: 'Your data' },
  { path: 'accessibility', title: 'Accessibility' },
  { path: 'pins', title: 'PINs' },
];

const settingsGenericChildren = GENERIC_SETTINGS_ROUTES.map(({ path, title }) => ({
  path,
  // The empty path must match FULLY or it claims every sibling's URL before
  // the router reaches them.
  ...(path ? {} : { pathMatch: 'full' as const }),
  loadComponent: () =>
    import('./features/profile/settings/settings-route-page.component').then(
      (m) => m.SettingsRoutePageComponent,
    ),
  data: { settingsRoute: path },
  title,
}));

const settingsRetiredChildren = Object.entries(RETIRED_ROUTE_REDIRECTS).map(
  ([from, to]) => ({
    path: from,
    redirectTo: to,
    pathMatch: 'full' as const,
  }),
);

export const routes: Routes = [
  // Help is entitled per customer (`help.centre`, off by default): without it the
  // guard sends the URL home, as for one that does not exist, and the API answers
  // NOT_FOUND on every Help operation.
  { path: 'help', data: { workspaceNav: true }, canActivate: [authGuard, featureFlagGuard('help.centre')], loadComponent: () => import('./features/help/help-centre.component').then(m => m.HelpCentreComponent) },
  { path: 'help/:guideId', data: { workspaceNav: true }, canActivate: [authGuard, featureFlagGuard('help.centre')], loadComponent: () => import('./features/help/help-centre.component').then(m => m.HelpCentreComponent) },
  { path: 'preview/walkthrough', data: { preview: true }, canActivate: [previewOnlyGuard], loadComponent: () => import('./features/walkthrough/walkthrough-preview.component').then(m => m.WalkthroughPreviewComponent) },
  { path: 'preview/help', data: { preview: true, workspaceNav: true }, canActivate: [previewOnlyGuard], loadChildren: () => import('./features/help/help-preview.routes').then(m => m.HELP_PREVIEW_ROUTES) },
  {
    // Louis's Home is the app's DEFAULT landing surface, served at `/home` for
    // everyone — see the `home` entry below.
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    // RESTARTS THE WELCOME TOUR over Home. The shell resets onboarding on any
    // navigation whose `urlAfterRedirects` contains `/welcome`, so this path
    // has to SERVE a page rather than redirect to one: it is the same Home
    // workspace as `/home`, under the path that asks for the tour.
    path: 'welcome',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/home-workspace-preview/home-workspace.routes').then(
        (m) => m.HOME_WORKSPACE_ROUTES,
      ),
    title: 'Welcome',
    data: { homeWorkspace: true },
  },
  // Phase 6A — legacy `home/knowledge*` URLs redirect to the new
  // `/flow/knowledge*` location. Placed ABOVE the `/home` parent so
  // Angular's matcher hits these before falling through to the parent
  // (which no longer owns a `knowledge` child).
  {
    path: 'home/knowledge',
    redirectTo: 'flow/knowledge',
    pathMatch: 'full' as const,
  },
  {
    path: 'home/knowledge/:workspaceId',
    redirectTo: 'flow/knowledge/:workspaceId',
    pathMatch: 'full' as const,
  },
  {
    path: 'home/knowledge/:workspaceId/:pageId',
    redirectTo: 'flow/knowledge/:workspaceId/:pageId',
    pathMatch: 'full' as const,
  },
  {
    path: 'home/knowledge/:workspaceId/:pageId/form',
    redirectTo: 'flow/knowledge/:workspaceId/:pageId/form',
    pathMatch: 'full' as const,
  },
  // The previous-generation Discover page (`features/news/`) was retired in
  // favour of the operational `/discover` surface, which fetches a strict
  // superset off the same services. Kept as a redirect (not a deletion) so
  // bookmarks and deep links already in circulation keep resolving. Placed
  // ABOVE the `/home` parent, matching the `home/knowledge*` precedent.
  { path: 'home/news', redirectTo: 'discover', pathMatch: 'full' as const },
  // ── OLD `/home/*` URLS ───────────────────────────────────────────────
  //
  // Everything in this block exists because `/home` changed hands three
  // times (the gen-1 Home, Home V3, Louis's Home). The article deep link used
  // to be a CHILD of this path; it is a redirect declared ABOVE it, following
  // the `home/knowledge*` precedent. The retired sub-pages (`my-day`,
  // `tasks`, and the gen-1 `home-legacy/*` pages) redirect from
  // `RETIRED_HOME_ROUTES`, below the `home` route. `/home/my-week`,
  // `/home/bookings` and `/home/services` are pages of Louis's Home itself.
  //
  // ⚠ `/home/article/:id` IS THE ONE THAT MATTERS. It is the target of every
  // article share this app has ever produced — `hp-feed.service.ts` still
  // builds that string — and it is pinned by `retired-home-routes.spec.ts`
  // (AC-H-8). It redirects to `/article/:id`, the app's one addressable
  // article, exactly as it did when it was a child of the legacy home.
  {
    path: 'home/article/:id',
    // The function form, so a share's query (`?utm_source=…`) survives the hop.
    redirectTo: toArticle,
    pathMatch: 'full' as const,
  },
  {
    // ⚠ LOUIS'S HOME IS `/home`, FOR EVERYONE (Nath, 4 October 2026: "All
    // other homes need to go, we need to make this the default").
    //
    // The Home workspace (features/home-workspace-preview) used to sit here
    // behind the `home-workspace` PostHog flag, with Home V3 below it as the
    // fall-through. The route gate and V3's definition have gone; the
    // `home-workspace-*` FEATURE flags inside the page are untouched.
    // `docs/HOME_RETIREMENT_PLAN.md` lists what retires and in what order.
    //
    // Real data is provided inside the lazy group (`home-workspace.routes.ts`),
    // off the app shell's graph. `homeWorkspace` in `data` is what the shell
    // reads to hand over its chrome — the key `/preview/home-workspace` carries
    // too.
    //
    // ─── HOME'S PAGES HAVE PATHS ────────────────────────────────────────
    //
    // `/home` is the dashboard; `/home/bookings`, `/home/services` and
    // `/home/visitors` are its pages, all served by the one Home route inside
    // the lazy group (`home-views.ts`, a matcher), so moving between them keeps
    // the page and its right-hand panel. Any other `/home/…` path does not
    // match there, and the router goes on to the routes below: the retired
    // sub-pages' redirects (`retired-home-routes.ts`), then the wildcard. The
    // redirects above (`home/knowledge*`, `home/news`, `home/article/:id`) are
    // declared first and win as before.
    //
    // ─── THE ANALYTICS COST ─────────────────────────────────────────────
    //
    // `domain` and `route_name` do not move: `route-taxonomy.ts` aliases
    // `home-preview`, `home-legacy` and `home` onto the one `home` domain.
    // `route_path` keeps its value, `/home`, but the page behind it changed on
    // the deploy date — worth an annotation on any series keyed on it.
    path: 'home',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/home-workspace-preview/home-workspace.routes').then(
        (m) => m.HOME_WORKSPACE_ROUTES,
      ),
    title: 'Home',
    data: { homeWorkspace: true },
  },
  {
    // SERVICES IS A PAGE OF HOME (`/home/services`, Nath 4 October 2026: "in
    // the same way that My Bookings is in the middle"). `/services` was its
    // address for a day and is in links already (Home's hand-offs, the nav,
    // notifications), so it redirects there with the whole query:
    //   ?service=<id>     that service's request, in Home's right-hand panel
    //   ?view=requests    My requests (the page now writes `?tab=requests`)
    //   ?highlight=<id>   one of My requests, in the panel
    path: 'services',
    pathMatch: 'full',
    redirectTo: redirectKeepingQuery(HOME_SERVICES_PATH),
  },
  {
    // APPROVALS (`/approvals`, 16 Oct 2026): the room bookings that wait for the viewer to approve or reject. It is the
    // landing place of the approval notification (`/approvals?booking=<id>`) and of the nav's Approvals entry, which only
    // someone holding `booking:approve` is offered. The API decides who may decide; anyone else sees an empty page.
    // A hidden view of Home in the registry, so the shell names where you are.
    path: 'approvals',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/approvals/approvals-page.component').then(
        (m) => m.ApprovalsPageComponent,
      ),
    title: 'Approvals',
  },
  // ── EVERY OLDER HOME ──────────────────────────────────────────────────
  //
  // Home V3's build-out path, the gen-2 Home (`/home-preview`) and the gen-1
  // Home (`/home-legacy`) with its sub-pages: each lands on Louis's Home,
  // with any shared post or article still open, and Services on the Services
  // page. The gen-2 Home's
  // communities and writer routes are not Homes and keep their paths (below).
  // The old Bookings and Services harnesses (`/preview/bookings`,
  // `/preview/services`) land on those pages in Home's live-shape harness.
  // The table and its reasons: `core/guards/retired-home-routes.ts`.
  ...RETIRED_HOME_ROUTES,
  {
    // Hosted meetings' join page (P7.04) in every state, with fixed data and
    // no API: `?state=too-early|open|waiting|lobby|denied|ended|revoked|full|
    // guests-off|removed|guest-code|guest-verify|notice|stage`.
    path: 'preview/flow-join',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-meetings/preview/flow-join-preview.component').then((m) => m.FlowJoinPreviewComponent),
    title: 'Join page preview',
    data: { preview: true },
  },
  {
    // The hosted-meeting call stage (P7.05b) with fixed people:
    // `?people=1..25&share=0|1&captions=0|1&host=0|1&guest=0|1&lobby=0..5&notes=off|recording|paused|stopping`.
    path: 'preview/call-stage',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-meetings/preview/call-stage-preview.component').then((m) => m.CallStagePreviewComponent),
    title: 'Call stage preview',
    data: { preview: true },
  },
  {
    // Flow Live's call dock over a page (0sm8.3–.5), fixed people, no call:
    // `?state=connecting|ringing|live|muted|recording|reconnecting|failed&video=0|1&others=0|1`.
    path: 'preview/call-dock',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/calls/preview/call-dock-preview.component').then((m) => m.CallDockPreviewComponent),
    title: 'Call dock preview',
    data: { preview: true, surface: 'dock' },
  },
  {
    // The incoming-call card: `?kind=ring|knock&busy=0|1&count=1|2`.
    path: 'preview/call-incoming',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/calls/preview/call-dock-preview.component').then((m) => m.CallDockPreviewComponent),
    title: 'Incoming call preview',
    data: { preview: true, surface: 'incoming' },
  },
  {
    // Pages without a session: Flow's shell, the page tree, the overview and
    // the real editor on an in-memory workspace, against an in-page stand-in
    // for the collaboration server (`features/pages-preview/`). `?latency=<ms>`
    // slows every answer, to watch a page open.
    path: 'preview/flow',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/pages-preview/pages-preview.routes').then((m) => m.PAGES_PREVIEW_ROUTES),
    title: 'Pages preview',
    data: { preview: true },
  },
  {
    // WRITE-PATH MONITOR — the only /preview route that drives the REAL seam.
    // Every Send here is a real booking, so it is dev-only and says so on the
    // page. `loadChildren` keeps its providers (and ScheduleRealDataService)
    // out of the initial bundle.
    path: 'preview/flow-monitor',
    // `loadComponent`, NOT `loadChildren`. A child route at `path: ''` gave
    // the nested schedule surface nothing to anchor its `navigate([], {
    // relativeTo })` to, so it resolved to the root and the page bounced
    // itself to `#/` on load. The providers live on the component instead,
    // which keeps them in this lazy chunk.
    loadComponent: () =>
      import('./features/flow-monitor-preview/flow-monitor-page.component').then(
        (m) => m.FlowMonitorPageComponent,
      ),
    title: 'Write-path monitor',
    data: { preview: true },
  },
  {
    // Louis's fixtures, with Home's pages at their paths as on `/home`
    // (`/preview/home-workspace/services`; `home-views.ts`).
    path: 'preview/home-workspace',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/home-workspace-preview/home-workspace.routes').then(
        (m) => m.HOME_WORKSPACE_PREVIEW_ROUTES,
      ),
    title: 'Home workspace preview',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // The Home workspace's LIVE shape without a session: the /home data contract
    // fed sample API rows through the real mappings, in four scenarios
    // (?scenario=normal|empty|error|slow) and with ?flags= for the no-API parts.
    path: 'preview/home-workspace-live',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/home-workspace-preview/home-workspace-live-preview').then(
        (m) => m.HOME_WORKSPACE_LIVE_PREVIEW_ROUTES,
      ),
    title: 'Home workspace live preview',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // The Plan my week proposal (home-week-plan lane, phase 1): Louis's This week with the one week planner over it,
    // on a sample week. No session, nothing booked. See docs/PLAN_MY_WEEK_PROPOSAL.md.
    path: 'preview/plan-week',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/plan-week-preview/plan-week-preview.page').then(
        (m) => m.PlanWeekPreviewPageComponent,
      ),
    title: 'Plan my week proposal',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // Spotlight (PLAN 1.6/1.7, beads t900.4/.11): the widget states (board 3.2), the compact and phone sizes
    // (3.3) and the reader on the Final 5 examples. Fixtures only, no session, nothing sent. ?theme=.
    path: 'preview/spotlight',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/spotlight/preview/spotlight-preview.routes').then((m) => m.SPOTLIGHT_PREVIEW_ROUTES),
    data: { preview: true },
  },
  {
    // The people kit (lane people-kit, bead z12i.39): one employee search, chip and card on sample people. No
    // session, nothing sent. ?scenario=normal|empty|error|slow, ?smart=0, ?theme=. See the page's header.
    path: 'preview/people-kit',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/people-kit-preview/people-kit-preview.page').then(
        (m) => m.PeopleKitPreviewPageComponent,
      ),
    title: 'People kit',
    data: { preview: true },
  },
  {
    // A colleague's full profile (/people/:id) on a sample, without a session (lane person-panel, bead z12i.54):
    // ?state=loading|error|missing|quiet, ?presence=office|booked|home|none, ?theme=. See public-profile-preview.ts.
    path: 'preview/people/:id',
    canActivate: [previewOnlyGuard],
    providers: publicProfilePreviewProviders(),
    loadComponent: () =>
      import('./features/people/public-profile-page.component').then(
        (m) => m.PublicProfilePageComponent,
      ),
    title: 'Profile preview',
    data: { preview: true },
  },
  {
    // ⚠ THE FLIP. `/discover` IS NOW THE NEW SURFACE.
    //
    // It stays as a path because it is a live deep-link target — shared links,
    // push notifications, the reader's back button, five call sites across
    // home, live-events and the composer — and every one of them should land
    // on the rebuilt Hubs rather than on the retired page. A redirect catches
    // all of them at once, which is why this is one line rather than fourteen
    // edited navigate() calls.
    //
    // Angular preserves query params through a relative `redirectTo`, so
    // `?view=stream` and a shared `?post=` survive the hop.
    //
    // The old feature is NOT deleted here. It is 199 files and it still owns
    // shorts, starred and the games centre, none of which the new surface has
    // yet; deleting it in the same change as the flip would take working
    // features away to ship a routing decision. It comes out when Hubs reaches
    // parity (bead fv0.14), and until then `/preview/discover` keeps it
    // reachable for comparison.
    // ⚠ ONE DEFINITION, AND THE REDIRECT IS A GUARD.
    //
    // `redirectWhenFlagOn` sends `/discover` to `/hubs` while `content.hubs`
    // is on, and returns true when it is off — so the flag off serves the
    // PREVIOUS Discover page from this same route, recovered verbatim from
    // `f268ea66f^`, the commit that replaced it with a bare redirect. That is
    // the rollback: the flip used to be gated on `content.discover`, the same
    // key as `/hubs` itself, so turning Hubs off removed the redirect's TARGET
    // and bounced the tenant to the home page.
    //
    // ⚠ AND IT IS A GUARD BECAUSE `canMatch` + `redirectTo` IS ILLEGAL.
    // Angular validates the router config at bootstrap and raises NG04014 —
    // "redirectTo and canMatch cannot be used together. Redirects happen
    // before guards are executed." — which blanks the ENTIRE application, not
    // just this route. `ng build` compiles it without a word. The first
    // attempt here was exactly that pair of definitions and it took the app
    // down; see `redirectWhenFlagOn` for the supported shape.
    path: 'discover',
    loadComponent: () =>
      import('./features/discover/discover-page.component').then(
        (m) => m.DiscoverPageComponent,
      ),
    providers: [
      { provide: GamesApi, useClass: GamesGraphqlApi },
      DailyGameStore,
    ],
    canActivate: [
      authGuard,
      featureFlagGuard('content.discover'),
      redirectWhenFlagOn('content.hubs', ['/hubs']),
    ],
    title: 'Hubs',
    data: { preview: false, answerTransport: 'simulate' },
  },
  {
    // Organisation video library — Shorts and full videos from owned Mux
    // playback on posts and workplace events. Viewer destination, not a
    // Content Studio tab (Studio has no Videos section on purpose).
    path: 'videos',
    loadComponent: () =>
      import('./features/videos/videos-page.component').then(
        (m) => m.VideosPageComponent,
      ),
    canActivate: [authGuard],
    title: 'Videos',
    data: { preview: false },
  },
  {
    // The AI panel's widget layer, on `runDemo()`'s fixture transcript. The
    // panel had no harness until now, which is how a doubled border that
    // came apart on hover survived in BOTH renderers that share these
    // widgets (this panel and the full page at `/ai`).
    path: 'preview/ai-panel',
    canActivate: [previewOnlyGuard],
    // Its own conversation, answered by the simulated gemba: what you type
    // streams back (delta / widget / message) without a backend.
    providers: simulatedAskProviders(),
    loadComponent: () =>
      import('./features/shell/components/ai-panel/ai-panel-preview.component').then(
        (m) => m.AiPanelPreviewComponent,
      ),
    title: 'AI panel preview',
    data: { preview: true },
  },
  {
    // The full-page chat on the same fixture transcript, without a session:
    // the shipped `/ai` component, seeded by its own `?aiDemo=1` hook. Both
    // renderers now draw widgets through `im-ai-widget-outlet`, and this is
    // the only way to see the page's half of that without signing in.
    path: 'preview/ai-chat',
    canActivate: [previewOnlyGuard],
    // Simulated gemba, as above. `?aiDemo=1` seeds every widget kind;
    // `?aiDemo=stream` starts empty and streams one answer on load.
    providers: simulatedAskProviders(),
    loadComponent: () =>
      import('./features/ai-chat/ai-chat-page.component').then((m) => m.AiChatPageComponent),
    title: 'AI chat preview',
    data: { preview: true },
  },
  {
    // The Ask kit on its own (shared/ask/): one surface in any frame
    // (`?mode=panel|sheet|page|dock|composer`), without a session. The real
    // fetch by default, so `e2e/ask-kit.spec.ts` answers `/api/conversation`;
    // `?sim=1` answers with the simulated gemba and `?fail=<kind>` fails it.
    path: 'preview/ask',
    canActivate: [previewOnlyGuard],
    providers: askPreviewProviders(),
    loadComponent: () =>
      import('./features/ai-chat/ask-preview.component').then((m) => m.AskPreviewComponent),
    title: 'Ask preview',
    data: { preview: true },
  },
  {
    // Ask docked beside a meeting draft (lane ask-meeting): a stand-in editor registered as the schedule's
    // is, and a simulated gemba answering the meeting starters with meetingDraftPatch widgets. No session.
    // `?people=8` overfills the room, `?clash=1` adds a clash, `?attendees=0` starts empty.
    path: 'preview/ask-meeting',
    canActivate: [previewOnlyGuard],
    providers: askMeetingPreviewProviders(),
    loadComponent: () =>
      import('./features/ai-chat/ask-meeting-preview.component').then((m) => m.AskMeetingPreviewComponent),
    title: 'Ask on a meeting (preview)',
    data: { preview: true },
  },
  {
    // Ask on the map (lane ask-on-map, 5 Oct): a stand-in map connected to MapAskBridge that lists every
    // pin, focus and route Ask asks of it, beside the shipped Ask surface talking to a simulated gemba that
    // answers the map starters with entities and mapRoute. No session.
    path: 'preview/ask-map',
    canActivate: [previewOnlyGuard],
    providers: askMapPreviewProviders(),
    loadComponent: () =>
      import('./features/ai-chat/ask-map-preview.component').then((m) => m.AskMapPreviewComponent),
    title: 'Ask on the map (preview)',
    data: { preview: true },
  },
  {
    // Ask's Smart fill on My week (lane ask-week-plan, 5 Oct): a stand-in My week registered with
    // AskWeekService (context.week, apply, undo) beside the shipped Ask surface, talking to a simulated gemba
    // that answers with weekPlan@1 built from the week the request carried. No session.
    path: 'preview/ask-week',
    canActivate: [previewOnlyGuard],
    providers: askWeekPreviewProviders(),
    loadComponent: () =>
      import('./features/ai-chat/ask-week-preview.component').then((m) => m.AskWeekPreviewComponent),
    title: 'Ask plans your week (preview)',
    data: { preview: true },
  },
  {
    // The Ask companion (bead z12i.25): the floating Ask that stays with you,
    // over Home, the Calendar and the demo-floor map, on its own simulated
    // conversation and stored choices. Every look, status and scene by URL.
    path: 'preview/ask-companion',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/ask-companion/preview/ask-companion-preview.page').then((m) => m.AskCompanionPreviewPage),
    title: 'Ask companion preview',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // First run on Paper's onboarding, on fixtures (no session, no profile):
    // every page, theme, accent, tenant and prefill by URL. The review and
    // Playwright harness for `im-first-run`; see first-run-preview.page.ts.
    path: 'preview/first-run',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/first-run/preview/first-run-preview.page').then((m) => m.FirstRunPreviewPage),
    title: 'First run preview',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // The floating-Ask decision harness (bead z12i.24): three ways to float Ask
    // over Home, the schedule and the demo-floor map, answered by the simulated
    // gemba (the page provides its own conversation). `homeWorkspace` mounts
    // Louis's navigation, which every option has to live with.
    path: 'preview/ask-dock',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/ask-dock-preview/ask-dock-preview.page').then((m) => m.AskDockPreviewPage),
    title: 'Floating Ask preview',
    data: { preview: true, homeWorkspace: true },
  },
  {
    // Public design/e2e harness — real component on deterministic fixtures.
    path: 'preview/discover',
    canActivate: [previewOnlyGuard],
    // The page and its mock games API load with the route: named here, the
    // mock's rooms hub reached the initial bundle (see the routes file).
    loadChildren: () =>
      import('./features/discover/discover-preview.routes').then(
        (m) => m.DISCOVER_PREVIEW_ROUTES,
      ),
    providers: [
      BROADCAST_ACKNOWLEDGE_PREVIEW_PROVIDER,
      BROADCAST_EXTERNAL_CLICK_PREVIEW_PROVIDER,
    ],
    title: 'Hubs preview',
    data: { preview: true },
  },
  {
    // ── THE REBUILT HUBS SURFACE ────────────────────────────────────────
    // Built against `contentFeed` — server-arranged, cursor-paged — rather
    // than the `articles(skip/take)` pool `/discover` still reads. It runs
    // ALONGSIDE `/discover` until it reaches parity, so nothing
    // half-migrated is ever the live page; the flip is one commit that
    // redirects `/discover` here and deletes the old feature.
    //
    // The capability key is `content.discover` on purpose: it is the
    // server's, it gates the same content, and a second flag would let a
    // customer end up entitled to one surface and not the other.
    path: 'hubs',
    loadComponent: () =>
      import('./features/hubs/hubs-page.component').then(
        (m) => m.HubsPageComponent,
      ),
    // ⚠ `warmChunk` DECIDES NOTHING. It starts the chunk above downloading
    // alongside the guards, because Angular fetches a lazy component only
    // once they have all passed — so the biggest download on this route was
    // sitting behind a network wait that had no use for it. Same import, so
    // the module registry dedupes.
    //
    // Guards run CONCURRENTLY (`prioritizedGuardValue` is a `combineLatest`),
    // so its position here is for the reader, not the router. See
    // `warm-chunk.guard.ts` for what that does and does not buy.
    // ⚠ ANY-OF, FOR ONE RELEASE. `content.hubs` is new, and a viewer whose
    // catalogue has not yet published it would otherwise lose the surface the
    // moment this ships — `FeatureFlagsViewerService.is()` fails CLOSED for a
    // key it does not know. Keeping `content.discover` as the second key means
    // today's entitled tenants keep Hubs whatever their catalogue says.
    //
    // NARROWS to `featureFlagGuard('content.hubs')` once the catalogue key is
    // confirmed live in every environment. Until then the kill switch works in
    // one direction only: turning `content.hubs` OFF still restores Discover
    // through the two-definition route above, because that decision is made by
    // `canMatch` on `/discover` and not here.
    canActivate: [
      authGuard,
      warmChunk(() => import('./features/hubs/hubs-page.component')),
      featureFlagAnyGuard(['content.hubs', 'content.discover']),
    ],
    title: 'Hubs',
    data: { preview: false },
  },
  {
    // STARTING A HUB. A route rather than a dialog, and the reasons are all
    // the reasons a three-step flow is not a sheet: the browser's Back button
    // walks the steps (the step rides in `?step=`), the URL can be sent to
    // somebody, and a refusal is decided before anything renders instead of
    // by a button that is missing from a page you are already looking at.
    //
    // ⚠ IT MUST SORT BEFORE `hubs/:something` IF ONE IS EVER ADDED. There is
    // no hub-id route today — the feed addresses hubs with `?hub=` — but a
    // hub called "new" is a hub somebody will make.
    path: 'hubs/new',
    loadComponent: () =>
      import('./features/hubs/create/hub-create-page.component').then(
        (m) => m.HubCreatePageComponent,
      ),
    canActivate: [
      authGuard,
      warmChunk(
        () => import('./features/hubs/create/hub-create-page.component'),
      ),
      // The same any-of pair the feed carries, for the same one release: a
      // viewer whose catalogue has not published `content.hubs` yet must not
      // lose the surface, and `is()` fails CLOSED for a key it does not know.
      featureFlagAnyGuard(['content.hubs', 'content.discover']),
      // ⚠ AND THE ACTION, WHICH IS THE HALF THAT ACTUALLY GATES CREATION.
      // `community:create` is granted to everyone by default and is the grant
      // a customer revokes to restrict who may start a hub. The nav row is
      // already hidden on it (`hubs-page.canCreateHub`), but a hidden row is
      // not a gate — a pasted URL never passes through the nav. All four
      // mutations behind this page are `@RequireAction(COMMUNITY_CREATE)` on
      // the API, so this is the client half of a rule the server enforces,
      // never a substitute for it.
      anyActionGuard(['community:create']),
    ],
    title: 'Start a hub',
    data: { preview: false },
  },
  {
    // A HUB'S JOIN LINK (hub invites, 12 Oct 2026). The link the API returns
    // from createHubJoinLink points here. The token is a credential, so it
    // never reaches the address bar of the page that handles it: the guard
    // parks it for this tab (hub-join-park.ts) and sends the reader on to a
    // bare /hubs, which applies the hubs guards (auth, the any-of flag) and
    // opens the join card from the parked key. Signed out, the auth bounce
    // carries /hubs to login-web, never the token.
    path: 'hubs/join/:token',
    canActivate: [hubJoinGuard('/hubs')],
    children: [],
  },

  {
    // The writer with no session (articles-v2): start, compose, drafts, reopen
    // and the published article, on the real pages with an in-memory
    // transport. `/write` is behind `authGuard` and had no harness, so the
    // writer could not be reviewed or journey-tested without a login. See
    // `features/write/preview/write-preview.services.ts`.
    path: 'preview/write',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/write/preview/write-preview.routes').then(
        (m) => m.WRITE_PREVIEW_ROUTES,
      ),
    title: 'Write preview',
    data: { preview: true },
  },
  {
    // Guard-free design/e2e harness for the creation flow. Every read behind
    // it is viewer-scoped, so unauthenticated the subject picker has no
    // subjects and the people search finds nobody — a page that looks broken
    // rather than one with no session. The WRITE is stubbed too, which the
    // other preview routes do not need: this page's third act is what happens
    // after the press, and without a fake create none of it is reviewable.
    path: 'preview/hubs/new',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/hubs/create/hub-create-page.component').then(
        (m) => m.HubCreatePageComponent,
      ),
    // The done step's Invite people and Copy the join link answer from memory too.
    // The route-local viewer gives the kept draft its viewer and customer key
    // (review, 13 Oct 2026); without one there is no draft at all.
    providers: [HUBS_PREVIEW_VIEWER_PROVIDER, HUB_DIRECTORY_PREVIEW_PROVIDER, HUB_CREATE_PREVIEW_PROVIDERS, HUB_INVITES_PREVIEW_PROVIDER],
    title: 'Start a hub (preview)',
    data: { preview: true },
  },
  {
    // The join link, in the harness: the same parking as hubs/join/:token.
    path: 'preview/hubs/join/:token',
    canActivate: [hubJoinGuard('/preview/hubs')],
    children: [],
    data: { preview: true },
  },

  {
    // Guard-free design/e2e harness for the surface above. `contentFeed` is
    // viewer-scoped, so unauthenticated it 401s and the wall would be an error
    // banner — the provider serves the prototype's own corpus instead, through
    // the real service's paging and state.
    path: 'preview/hubs',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/hubs/hubs-page.component').then(
        (m) => m.HubsPageComponent,
      ),
    providers: [
      HUB_FEED_PREVIEW_PROVIDER,
      HUB_PAGE_PREVIEW_PROVIDER,
      CONTENT_ENGAGEMENT_PREVIEW_PROVIDER,
      provideHubsCommentsPreview(),
      HUB_DIRECTORY_PREVIEW_PROVIDER,
      EDITORS_PICKS_PREVIEW_PROVIDER,
      // The one WRITE seam. Every provider above it is read-side; without
      // this the composer could be opened here and never completed, because
      // an unauthenticated `createPost` 401s.
      HUB_COMPOSER_PREVIEW_PROVIDER,
      // Seeds a broadcaster, so the permission-gated half of the composer —
      // the Go live card and its stream-key path — is reviewable at all.
      HUBS_PREVIEW_VIEWER_PROVIDER,
      // The band was the ONE read on this page with no preview, so it
      // rendered nowhere in the harness — which is why two defects in its
      // cards had to be reported from a real session instead of caught here.
      COMMS_BAND_PREVIEW_PROVIDER,
      // Dean's Home (hubs.home-v2, 12 Oct 2026): the two pinned top stories,
      // the Everything rail (office, week, on now) and the heads-up strip, all
      // viewer-scoped reads. ?home=v1 turns the flag off in the harness.
      HOME_TOP_PREVIEW_PROVIDER,
      HUBS_RAIL_PREVIEW_PROVIDER,
      HUBS_HEADS_UP_PREVIEW_PROVIDER,
      // Spotlight PLAN 7.2: the hub's Spotlights tab, on fixtures.
      { provide: SPOTLIGHT_PLACES_DATA, useExisting: SpotlightPlacesHubPreviewData },
      // A Spotlight opened in the hub reads its widgets and answers from memory
      // too (their root default is the API, t900.23).
      provideSpotlightFixtureSources(),
      // A hub's Events and Challenges sections, and the new-event form: every
      // read is viewer-scoped, and the writes change the harness's copy only.
      HUB_EVENTS_PREVIEW_PROVIDER,
      HUB_CHALLENGES_PREVIEW_PROVIDER,
      // Plan a visit (Paper row 12, 13 Oct 2026): five open days at Rum Runner
      // Works with a refused day, a held desk and a status to undo; the writes
      // land in memory. ?visit=open|closed|in picks today's state.
      PLAN_VISIT_PREVIEW_PROVIDER,
      // Hub insights (board 14): manager-only and viewer-scoped. Coffee Club is
      // a game hub whose 7 days are quiet (every count suppressed); Rum Runner
      // Works is an office with no join history.
      HUB_INSIGHTS_PREVIEW_PROVIDER,
      // The members sheet's people search (Dean's review, 9 Oct 2026): the
      // same colleagues the creation harness offers.
      { provide: HubPeopleService, useClass: HubPeoplePreviewService },
      // Hub invites (12 Oct 2026): the sheet, the owner's list and the join
      // card answer from memory. ?hub=padel&invite=inv-padel opens an invite
      // from Dean; ?join=preview-link opens the same hub by link.
      HUB_INVITES_PREVIEW_PROVIDER,
      // "Tell another hub" (13 Oct 2026, sb82.21): the share writes and the
      // card's Join answer from memory. ?hub=lab carries a card for Padel Club
      // and one for a hub that has closed; ?cardjoin=fail refuses every join.
      HUB_CARD_PREVIEW_PROVIDER,
      // Explore (11 Oct 2026): every read is viewer-scoped, so the harness
      // answers from an in-memory tenant large enough to show the shelves,
      // and the interest catalogue behind Browse by and the typeahead.
      HUB_EXPLORE_PREVIEW_PROVIDER,
      // Saved (12 Oct 2026): myStarredContent is viewer-scoped, so the shelf
      // answers from memory; ?saved=empty shows the empty state.
      HUB_SAVED_PREVIEW_PROVIDER,
      { provide: HubInterestsService, useClass: HubInterestsPreviewService },
      // `content.creation` (articles-v2): the Article card is offered when the
      // tenant has authoring and the viewer may write; without it the harness
      // could never show article creation at all, which is what Nath found.
      // `hubs.home-v2`: Dean's Home on Everything (the pinned stories, the rail,
      // the heads-up; off with ?home=v1) and read state (dots, the caught-up
      // line, folds and the new-posts pill; off with ?readstate=off).
      { provide: HUBS_PREVIEW_FLAGS, useValue: ['communities.integrations', 'games', 'communities', 'content.creation', 'hubs.home-v2'] },
      // `hubs.home-v2` also draws the place hero on a hub's own page (board
      // 05); its Top here and High scores answer from memory.
      HUB_PLACE_PREVIEW_PROVIDER,
      // Visits and the seen beacon write into window.__hubsPreview instead.
      HUB_READ_STATE_PREVIEW_PROVIDER,
      // Acknowledging the band's must-read answers from the clock, so the
      // caught-up line's "1 must-read still waiting" can be settled here.
      BROADCAST_ACKNOWLEDGE_PREVIEW_PROVIDER,
      // The card hands over to the writer; in the harness that is the writer's
      // harness, so the journey stays signed-out end to end.
      { provide: ARTICLE_ROUTES, useValue: PREVIEW_ARTICLE_ROUTES },
    ],
    title: 'Hubs preview',
    data: { preview: true },
  },
  {
    // Guard-free harness for the rebuilt Settings area. All fifteen pages are
    // reachable from the rail inside the page — NOT from the preview shell's
    // bar, which renders Home's tile and views on every `/preview/*` route.
    //
    // Half fixture, half real. The twelve declarative pages are driven from a
    // value map through the real shell; Vehicles, Notifications and Delegation
    // are the REAL components on their real services, behind the preview
    // providers the harness applies to itself. Those providers sit on the
    // component and not on this entry on purpose — a route `providers` array
    // takes a value rather than a loader, so naming one here would pull its
    // whole import graph into the entry bundle (see
    // `broadcast-publisher-preview.providers.ts`, which measured it).
    path: 'preview/settings',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/profile/settings/preview/settings-preview-page.component').then(
        (m) => m.SettingsPreviewPageComponent,
      ),
    title: 'Settings (preview)',
    data: { preview: true },
  },
  {
    // No-auth harness for Settings › Your voice and Flow's welcome card (the
    // flow-voice lane): a stand-in API with `?state=none|saved|rerecord|off`,
    // `?record=1` and `?refuse=<REASON>`. A recording made here never leaves
    // the browser.
    path: 'preview/flow-voice',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/voice/flow-voice-preview.component').then(
        (m) => m.FlowVoicePreviewComponent,
      ),
    title: 'Your voice (preview)',
    data: { preview: true },
  },
  {
    // Public design/e2e harness for the Inbox (the bell's, in the shell's
    // floating column): the real component and the real notification centre
    // on deterministic `myInbox` documents — expired arrivals, a suggestion
    // card, a resolved card — which the real API cannot be made to send.
    path: 'preview/inbox',
    canActivate: [previewOnlyGuard],
    // A lazy group, so its stub transport and fixtures stay out of the initial bundle.
    loadChildren: () => import('./shared/inbox/inbox-preview.component').then((m) => m.INBOX_PREVIEW_ROUTES),
    title: 'Inbox preview',
    data: { preview: true },
  },
  {
    // The harness's older name, kept so bookmarks and older specs still land.
    path: 'preview/notifications',
    canActivate: [previewOnlyGuard],
    loadChildren: () => import('./shared/inbox/inbox-preview.component').then((m) => m.INBOX_PREVIEW_ROUTES),
    title: 'Inbox preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the READER'S ANALYTICS PANEL. The real
    // reader page, the real panel, the real entitlement rule and the real
    // `ReaderAnalyticsService` — only the transport and the identity are
    // substituted. `/article/:id` is behind `authGuard`, and reviewing this
    // panel on the live route additionally requires being the author or the
    // manager of a published article that people have actually read, which
    // makes a theme pass or an axe sweep impossible without a seeded tenant.
    path: 'preview/reader-analytics',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/reader/analytics/reader-analytics-preview.component').then(
        (m) => m.ReaderAnalyticsPreviewComponent,
      ),
    providers: READER_ANALYTICS_PREVIEW_PROVIDERS,
    title: 'Reader analytics preview',
    data: { preview: true },
  },
  {
    // No-auth harness for the space detail panel's LAYOUT — see the component.
    path: 'preview/space-panel',
    // Without this the idle preloader walks the harness inside the real app.
    data: { preview: true },
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/space-panel-preview/space-panel-preview.component').then(
        (m) => m.SpacePanelPreviewComponent,
      ),
  },
  {
    // No-auth harness for the map appearance panel — see the component for why
    // it exists. Short version: the panel lives behind the auth guard on top of
    // a live Mapbox canvas, so its 21 hand-rolled controls have never been
    // reviewable, and it has twice been the largest single source of a
    // polish-audit regression.
    path: 'preview/map-settings',
    data: { preview: true },
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import(
        './features/map-settings-preview/map-settings-preview-page.component'
      ).then((m) => m.MapSettingsPreviewPageComponent),
  },
  {
    // Public design + e2e harness for Command K. The palette is shell chrome,
    // so every authenticated route that hosts it also needs a live backend, a
    // captured session and a capability-complete tenant — which makes it
    // unreviewable in exactly the situations review matters most (a nine-theme
    // design pass, an axe sweep, a keyboard audit). Real component, real
    // registry, real ranker; synthetic commands.
    path: 'preview/cmdk',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/shell/components/cmdk/cmdk-preview.component').then(
        (m) => m.CmdkPreviewComponent,
      ),
    title: 'Command K preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the SHORTS PLAYER — the Reels/TikTok
    // surface. A route rather than a screenshot because the three claims that
    // matter are all things jsdom cannot see: `scroll-snap-type: y mandatory`
    // is layout, autoplay-on-view is a media pipeline, and "a 720x1280 clip
    // fills the page while a 1280x720 one keeps its whole frame" is both. It
    // names the SEVEN REAL CLIPS in the dev bucket, whose measured pixel
    // dimensions are in the seeder's manifest — but ⚠ every one of those
    // fourteen urls answers 403 today (the bucket is private and this app has
    // no signed-url path), so `?case=media` shows the player's honest response
    // to a dead address, and `?case=frames` is the offline pair that proves
    // the aspect claim with no network at all.
    // `?case=media|shorts|frames|gap|bare|empty`, `?at=<index>`, `?calm=1`.
    path: 'preview/shorts',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/discover/shorts/shorts-preview.component').then(
        (m) => m.ShortsPreviewComponent,
      ),
    title: 'Shorts player preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the shared video player. NOT a
    // convenience: `viewChild('media')` never resolves under bare-vitest
    // JIT, so the player's two constructor effects never run there and a
    // removal probe deleted the keyboard transport, the scrubber seek, both
    // native-control guards and the whole UNSUPPORTED branch with the unit
    // suite staying green for every one of them. This route puts the real
    // component in a real browser on a synthetic asset built in-page (a
    // silent WAV blob — no network, no committed binary), and
    // `e2e/video-player-transport.spec.ts` is what actually proves the
    // wiring. Scenario comes from the URL: `?case=hls|broken|withheld|…`.
    path: 'preview/video-player',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./shared/media/video-player/video-player-preview.component').then(
        (m) => m.VideoPlayerPreviewComponent,
      ),
    title: 'Video player preview',
    data: { preview: true },
  },
  {
    path: 'preview/event-page',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/events/event-page-preview.component').then(
        (m) => m.EventPagePreviewComponent,
      ),
    title: 'Event page preview',
    data: { preview: true },
  },
  {
    // The Go Live console, drivable without credentials — see the
    // component's docblock for why this is the only end-to-end coverage
    // that surface can have in this repo today.
    path: 'preview/live-console',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/live-events/live-event-console-preview.component').then(
        (m) => m.LiveEventConsolePreviewComponent,
      ),
    title: 'Go Live console preview',
    data: { preview: true },
  },
  {
    path: 'preview/event-setup',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/events/event-setup-preview.component').then(
        (m) => m.EventSetupPreviewComponent,
      ),
    title: 'Event setup preview',
    data: { preview: true },
  },
  {
    path: 'preview/videos',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/videos/videos-preview.component').then(
        (m) => m.VideosPreviewComponent,
      ),
    providers: [OrganisationVideosService],
    title: 'Videos preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the rotating broadcast front page. NOT a
    // duplicate of `/preview/discover`: that route sets `preview: true`, and
    // `DiscoverPageComponent.ngOnInit` returns on its FIRST LINE when that is
    // set — the front page's fetch is inside it, so the existing Discover
    // harness renders the band with an empty playlist and can prove nothing
    // about rotation, publishing windows or the editor's-pick chip.
    //
    // This route feeds the real band real WIRE DTOs through the real
    // `frontPageSlotsFromResponse` adapter, and takes its CLOCK from the URL —
    // `?at=before|during|after` moves `nowMs` and nothing else, which is how
    // "a slot outside its window stops rendering without anyone unpublishing
    // it" becomes an assertion rather than a claim. The clock reaches both the
    // band's `[nowMs]` input and the harness's own fact row through ONE seam —
    // `BROADCAST_CLOCK`, provided below — so the page cannot report an instant
    // the band never saw. Scenarios:
    // `?case=playlist|window|picks|external|dwell|live`. Proved by
    // `e2e/broadcast-front page-editorial.spec.ts` and
    // `e2e/discover-front page-journeys.spec.ts`.
    path: 'preview/front-page',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/discover/bands/broadcast-front-page-preview.component').then(
        (m) => m.BroadcastFrontPagePreviewComponent,
      ),
    providers: BROADCAST_FRONT_PAGE_PREVIEW_PROVIDERS,
    title: 'Broadcast front page preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the broadcast PUBLISHER board — the only
    // surface that reports a mandatory slot's acknowledgement rate back to the
    // person who made it compulsory (§6).
    //
    // It cannot be reviewed against the running stack: it needs a session, an
    // admin role, `broadcast.playlist` resolved ON, the `broadcast:publish`
    // action, AND a database that happens to hold a mandatory slot in the
    // current week with a counted audience. That last one is what makes a
    // browser journey impossible there — a test that passes because somebody
    // published a must-read on Tuesday is not a test.
    //
    // Real page, real `broadcast-wire.ts` mappers, real `broadcast-rules.ts`;
    // only the transport is replaced. The CLOCK comes from `BROADCAST_CLOCK`,
    // which `?at=monday|wednesday|sunday` moves and which nothing else on the
    // page varies with — the wire fixtures are identical across all three.
    // Scenarios: `?case=rate|unmeasured`. Proved by
    // `e2e/broadcast-publisher-acknowledgement.spec.ts`.
    path: 'preview/broadcast-publisher',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/settings/pages/broadcast/broadcast-publisher-preview.component').then(
        (m) => m.BroadcastPublisherPreviewComponent,
      ),
    providers: BROADCAST_PUBLISHER_PREVIEW_PROVIDERS,
    title: 'Broadcast publisher preview',
    data: { preview: true },
  },
  {
    // Public design + e2e harness for the Studio's FRONT PAGE — the loop
    // strip, the five-week lane board, the slot column and the picker, over
    // one board fetch.
    //
    // NOT `preview/front-page`, which is a different surface with a confusable
    // name: that one is the DISCOVER CONSUMER's band, `features/discover/bands`,
    // and it shows what a reader sees. This is what a curator sees. Colliding
    // on the path would have made every existing link to the reader's harness
    // silently open the editor's.
    //
    // Real page, real service, real mappers, real rules; the transport, the
    // clock, the viewer and the location's calendar are the board harness's
    // own, spread rather than copied. Scenarios: `?case=rate|unmeasured`,
    // `?at=monday|wednesday|sunday`.
    path: 'preview/front-page-studio',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/content/front-page/front-page-studio-preview.component').then(
        (m) => m.FrontPageStudioPreviewComponent,
      ),
    providers: FRONT_PAGE_STUDIO_PREVIEW_PROVIDERS,
    title: 'Front page studio preview',
    data: { preview: true },
  },
  {
    // Managed PDFs, images and hosted QR codes open inside the employee shell.
    // Generic web apps stay external via AppLaunchService.
    path: 'apps/:id',
    loadComponent: () =>
      import('./features/apps/app-resource-viewer.component').then(
        (m) => m.AppResourceViewerComponent,
      ),
    canActivate: [authGuard],
    title: 'App resource',
  },
  {
    // The Communities directory, full-width and standalone (features/communities).
    // It was `/home-preview/communities` while the gen-2 Home owned the path;
    // that URL redirects here (`retired-home-routes.ts`). The page itself
    // re-checks the `communities` capability (the BE enforces viewability on
    // every list query) and shares the singleton CommunitiesService store.
    path: 'communities',
    loadComponent: () =>
      import('./features/communities/hp-communities.component').then(
        (m) => m.HpCommunitiesComponent,
      ),
    canActivate: [authGuard],
    title: 'Communities',
  },
  {
    // A community, full-width. It was `/home-preview/community/:id`, which
    // redirects here with its id and query (`retired-home-routes.ts`).
    // Reachable directly (cold deep-link): the component re-runs the
    // `communities` capability gate + viewability check in ngOnInit (via
    // FeatureFlagsViewerService + CommunitiesService.getOne) and shows a
    // not-available state rather than leaking a community the viewer can't see.
    path: 'communities/:id',
    loadComponent: () =>
      import('./features/communities/hp-community-detail.component').then(
        (m) => m.HpCommunityDetailComponent,
      ),
    canActivate: [authGuard],
    // Fills CommunitiesService before activation so the shell bar's community
    // CONTEXT (crumb name + mark + sibling switcher) is correct on the FIRST
    // paint of a cold deep link, rather than flashing an unnamed crumb. Never
    // blocks entry — see the resolver.
    resolve: { communityContext: communityContextResolver },
    title: 'Community',
  },
  {
    // "New article" OPENS THE WRITER, on a blank article with the title
    // focused (articles-v2, 11 Oct 2026). There was a start page here — a
    // subject, guidance, sources and four shape cards before a word could be
    // typed — and Nath ruled it "really bad as its old style and over
    // complex". Its assisted path is "Draft with Ask" inside the empty
    // article now. `write/compose` below is the same writer and stays: Flow's
    // "Write an article" hands its draft over and navigates there.
    // The writer was under `/home-preview/write`; those URLs redirect here.
    path: 'write',
    loadComponent: () =>
      import('./features/write/compose/article-compose-page.component').then(
        (m) => m.ArticleComposePageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('content.creation')],
    title: 'Write an article',
  },
  {
    // The STUDIO COMPOSER, on a NEW unsaved article. A component per block —
    // `<im-article-canvas>` mounts the nine kinds — not a contenteditable.
    // `/write/:id` below opens the SAME component on an existing
    // one, so there is one writer and not two.
    //
    // ⚠ DECLARED BEFORE `write/:id`, and it has to be. Angular
    // matches in order, so with the param route first this opens the writer
    // on an article whose id is "compose".
    //
    // A handed-over draft (Flow's "Write an article") arrives through
    // `ArticleStartHandoffService` rather than the URL: the payload is a whole
    // draft body, and router state is serialised into the history entry, so a
    // refresh or a Back would re-apply it over the author's own edits.
    path: 'write/compose',
    loadComponent: () =>
      import('./features/write/compose/article-compose-page.component').then(
        (m) => m.ArticleComposePageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('content.creation')],
    title: 'Write an article',
  },
  {
    // "Your drafts" — the writer's own list of what is on the go.
    //
    // ⚠ DECLARED BEFORE `write/:id`, and it has to be. Angular
    // matches in order, so with the param route first `/write/
    // drafts` loads the EDITOR with the id "drafts" — a load for an article
    // that does not exist. Same rule as `coverage/new` before
    // `coverage/:id/edit` in admin.routes.ts.
    path: 'write/drafts',
    loadComponent: () =>
      import('./features/write/drafts/article-drafts-page.component').then(
        (m) => m.ArticleDraftsPageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('content.creation')],
    title: 'Your drafts',
  },
  // EDIT MODE, and it had no route at all. The Studio's row Edit control
  // linked to `/write/<id>`, which matched nothing and fell
  // through to the wildcard — so every Edit click landed on the home page.
  // Same component; it reads the id and hydrates.
  {
    // EDITING an existing article, in the STUDIO COMPOSER — the same component
    // `write/compose` loads, reading its `:id` and hydrating from
    // `getItemForEdit`. It used to load `ArticleEditorComponent`, so a new
    // article was written per block and an existing one was reopened in the
    // previous contenteditable: two writers, one of which nobody was still
    // building. `ArticleEditorComponent` stays on disk — other routes and its
    // own specs still reach it.
    //
    // ⚠ DECLARED AFTER `write/compose` and `write/drafts`, and it has to stay
    // there: Angular matches in order, and a param route above them would swallow
    // both as article ids.
    path: 'write/:id',
    loadComponent: () =>
      import('./features/write/compose/article-compose-page.component').then(
        (m) => m.ArticleComposePageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('content.creation')],
    title: 'Edit an article',
  },
  {
    // READING an article, as a page — and the FIRST deep link to an article
    // this product has ever had. `/home/article/:id` below looks like one and
    // is not: it loads the home page and opens a fixed overlay panel over it,
    // so the URL names the home page, not the article. Discover has no article
    // parameter at all.
    //
    // A SIBLING of `/discover`, like `/write` above, so it renders
    // full-width. NOT a bare route: it keeps the shell bar and adds its own
    // 44px sub-nav beneath it. "Full screen" here means the page is the
    // article, not that the app disappears.
    //
    // No `content.creation` flag — reading is not authoring, and `article:view`
    // is in DEFAULT_USER_ACTIONS. Gating a reader on the writer's flag would
    // hide published articles from everyone who cannot write one.
    path: 'article/:id',
    loadComponent: () =>
      import('./features/reader/article-reader-page.component').then(
        (m) => m.ArticleReaderPageComponent,
      ),
    canActivate: [authGuard],
    title: 'Article',
  },
  {
    // THE ONE "SEE ALL" LIST OF SPOTLIGHTS (Spotlight PLAN 7.3). Reached from
    // Home's "Spotlights for you" (going away until Louis designs it, Nath
    // 14 Oct), a hub's Spotlights tab, ⌘K and links — never
    // from the main nav (Nath's call 1, 12 Oct). A sibling of `/article/:id`,
    // full-width under the shell bar. `content.viewer` is the API's own gate
    // on `spotlightFeed`.
    path: 'spotlights',
    loadComponent: () =>
      import('./features/spotlight/places/spotlights-page.component').then(
        (m) => m.SpotlightsPageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('content.viewer')],
    title: 'Spotlights',
  },
  // ── Schedule ───────────────────────────────────────────────────
  {
    // ⚠ THE FLIP. `/schedule` IS THE REDESIGN.
    //
    // It was served at `/schedule-preview` while the previous-generation page
    // held this path. It no longer is: the redesign is the schedule every nav
    // affordance reaches, so it owns the canonical URL, and the previous page
    // moved to `/schedule-legacy` below.
    //
    // Runs on live data (ScheduleRealDataService: grid, calendar, creation
    // panel, catering, Booking on Behalf). The component renders its own
    // full-screen chrome over the shell.
    //
    // `scheduleLegacyRedirectGuard` runs BEFORE the ANY-OF gate and forwards
    // the two entries this surface cannot serve — a legacy-shaped URL
    // (`?type=`, `?tab=`, the booking-panel and filter params) and a tenant
    // without `rooms.booking`, whose grid would be a room grid they cannot
    // book. Everything else renders here, including in-app navigations: the
    // old guard's `router.navigated` short-circuit is gone, because the
    // question is now a property of the URL rather than of how you arrived.
    //
    // ANY-OF gate, unchanged and shared with `/schedule-legacy`: reachable
    // while at least one sub-view capability is on.
    path: 'schedule',
    loadComponent: () =>
      import('./features/schedule-preview/schedule-preview.component').then(
        (m) => m.SchedulePreviewComponent,
      ),
    /**
     * THE CALENDAR BETA IS LIVE HERE, DECLARED RATHER THAN INFERRED.
     *
     * `Sv3StoreService` otherwise works it out from the seam, and it cannot
     * know until the seam answers: `hasCalendar()` starts false and only
     * turns true once a fetch returns. So every load of this page rendered
     * the SAMPLE WEEK for the frames in between — invented meetings, on a
     * real person's calendar, reported from use twice.
     *
     * There is nothing to infer on this route. It sits behind `authGuard`, so
     * a viewer who reaches it has a session by definition. Saying so removes
     * the window entirely instead of narrowing it.
     *
     * The preview routes keep the inference, which is what they are for.
     */
    providers: [
      { provide: SV3_DATA_SOURCE, useValue: 'live' as const },
      // The app shell mounts `im-quick-book-dialog`, so the calendar's "Book"
      // affordances have something to open here. On the preview routes they
      // do not, and the token's default of false makes them say so rather
      // than fire into nothing.
      { provide: SV3_SHELL_DIALOGS, useValue: true },
    ],
    canActivate: [
      authGuard,
      scheduleLegacyRedirectGuard,
      featureFlagAnyGuard([
        'rooms.booking',
        'desks.booking',
        'parking.booking',
        'schedule.events',
      ]),
    ],
    /**
     * ⚠ WITHOUT THIS, A PASTED LEGACY LINK IS SILENTLY IGNORED. Angular's
     * default only re-runs guards when the PATH params change, and this app
     * routes on the hash — so pasting `#/schedule?type=desks` while already
     * on the schedule changes the document not at all and the path not at
     * all. The navigation happens, `scheduleLegacyRedirectGuard` never runs,
     * and the user gets the room grid with what they asked for dropped. That
     * is precisely the failure the guard exists to prevent, arriving by the
     * most likely route of all: a colleague sends you an old link while you
     * have the schedule open.
     *
     * A PREDICATE, NOT `paramsOrQueryParamsChange`. This page writes its own
     * query params constantly (`surface`, `date`, `offices`, and the one-shot
     * panel params it clears at the point of consumption), each as a
     * `replaceUrl` navigation. Re-running three guards — one of which awaits
     * the viewer — on every one of those writes would put an async gate in
     * front of the page's URL mirroring for no benefit, since none of those
     * keys is a legacy key. Narrowing it to "a legacy param that was not
     * there before has appeared" leaves the page's own writes untouched.
     */
    runGuardsAndResolvers: (from, to) =>
      LEGACY_SCHEDULE_PARAMS.some(
        (param) => param in to.queryParams && !(param in from.queryParams),
      ),
    title: 'Schedule',
  },
  {
    // The redesign's OLD URL, kept as a redirect and not deleted. It was the
    // default surface for months, so it is in bookmarks, pinned tabs, chat
    // messages, push payloads and signage playlists this repo cannot reach.
    //
    // Query params are carried across, which is the whole point: the deep
    // links in circulation are `/schedule-preview?event=…`,
    // `?surface=calendar` and `?date=…`, and a redirect that dropped them
    // would land people on the right page in the wrong place.
    path: 'schedule-preview',
    pathMatch: 'full',
    redirectTo: (r) =>
      inject(Router).createUrlTree(['/schedule'], {
        queryParams: r.queryParams,
      }),
  },
  {
    // Public preview harness for the Schedule redesign — NO auth guard
    // (mirrors `/preview/schedule-v2`), so the Playwright fidelity loop can
    // load it headless against the seeded mock data, which its routes provide
    // (the page used to fall through to the live service and draw nothing).
    // The component renders FULL-SCREEN over the app shell (its host is
    // `position:fixed; inset:0`), so it appears chrome-free without touching
    // the app-shell.
    path: 'preview/schedule-redesign',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/schedule-preview/schedule-redesign-preview.routes').then(
        (m) => m.SCHEDULE_REDESIGN_PREVIEW_ROUTES,
      ),
    title: 'Schedule redesign preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The meeting panel's Services tab on its own (lane 13), for the paired shots and the journeys. No auth, the schedule mock.
    path: 'preview/services-tab',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/schedule-preview/services-tab-preview.component').then((m) => m.ServicesTabPreviewComponent),
    title: 'Services tab preview',
    data: { preview: true },
  },
  {
    // The schedule motion kit's live specimen (features/schedule-preview/
    // motion/) — curves, named motions, spring, FLIP, morph and presence, so
    // the lanes building on the kit can see the vocabulary. No auth, no data,
    // its own lazy chunk; the same page is `?specimen=motion` on the harness.
    path: 'preview/schedule-motion',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/schedule-preview/motion/specimen/sp-motion-specimen.component').then(
        (m) => m.SpMotionSpecimenComponent,
      ),
    title: 'Schedule motion kit',
    data: { preview: true },
  },
  {
    // The app motion kit's specimen (core/motion) — pop from origin, morph,
    // FLIP, spring bounce-back and flyTo, for features outside the schedule.
    // No auth, no data, its own lazy chunk.
    path: 'preview/motion',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/motion-specimen/motion-specimen.component').then((m) => m.MotionSpecimenComponent),
    title: 'Motion kit',
    data: { preview: true },
  },
  {
    // Schedule v3 — the three-column calendar shell, built alongside
    // `/schedule-preview` rather than inside it. The two use opposite layout
    // models (grid tracks here, panels floating on a scrim there), so this is
    // a separate page and the shipping route is untouched.
    //
    // Preview-gated while the frame is reviewed: it runs on a fixed sample
    // week and reaches no service, so it needs no session. Unlike the
    // redesign route above, this page does NOT take over the screen — it
    // mounts inside `main.main-surface` so the shell's own navigation stays
    // exactly as it is.
    path: 'preview/schedule-v3',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/schedule-v3/sv3-preview-page.component').then(
        (m) => m.Sv3PreviewPageComponent,
      ),
    title: 'Schedule v3 preview',
    data: { preview: true },
  },
  {
    // The "Flow isn't switched on for you" page (the Flow capability, FC-6). No sign-in, no API. Dev only.
    // `?why=switched-off` is the variant for a person Flow was switched off under.
    path: 'preview/flow-off',
    canActivate: [previewOnlyGuard],
    loadComponent: () => import('./features/flow-v2/access/flow-off.page').then((m) => m.FlowOffPageComponent),
    title: 'Flow off preview',
    data: { preview: true },
  },
  {
    // Share, Invite, the faces and the mini profile on stand-in data (WP-39). No sign-in, no API. Dev only.
    path: 'preview/flow-share',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/share/preview/flow-share-preview.component').then((m) => m.FlowSharePreviewComponent),
    title: 'Flow share preview',
    data: { preview: true },
  },
  {
    // The caption corner in the real shell on a stand-in session (WP-31): the corner follows a person across three
    // stand-in pages. Dev only.
    path: 'preview/flow-caption-corner/:page',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./core/flow-caption-corner/preview/flow-caption-corner-preview.component').then(
        (m) => m.FlowCaptionCornerPreviewComponent,
      ),
    title: 'Flow caption corner preview',
    data: { preview: true },
  },
  {
    // The first-run sheet (WP-40) with stand-ins for the notice, the voice profile and "done": no sign-in, no API.
    // Dev only.
    path: 'preview/flow-first-run',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/first-run/preview/flow-first-run-preview.component').then(
        (m) => m.FlowFirstRunPreviewComponent,
      ),
    title: 'Flow first run preview',
    data: { preview: true },
  },
  {
    path: 'preview/flow-speaker-names',
    canActivate: [previewOnlyGuard],
    loadComponent: () => import('./features/flow-v2/meetings/preview/flow-speaker-names-preview.component').then(m => m.FlowSpeakerNamesPreviewComponent),
    title: 'Flow speaker names preview',
    data: { preview: true },
  },
  {
    // The Flow live session screen on synthetic data: no session, no
    // microphone, no backend (2e2o). The screen and its orchestrator are the
    // real ones; the harness provides only the edges, on itself. Dev only.
    // `?blocks=2000` is what the transcript's scroll measurement opens.
    path: 'preview/flow-live',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/live/preview/flow-live-preview.component').then(
        (m) => m.FlowLivePreviewComponent,
      ),
    title: 'Flow live session preview',
    data: { preview: true },
  },
  {
    // The live transcript's calm patching (WP-11) on a recorded patch stream: the real transcript, the real
    // reducer, no session and no backend. `?flags=off` is the transcript as it was before. Dev only.
    path: 'preview/flow-transcript-patches',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/live/preview/flow-transcript-patches-preview.component').then(
        (m) => m.FlowTranscriptPatchesPreviewComponent,
      ),
    title: 'Flow transcript patches preview',
    data: { preview: true },
  },
  {
    // The ended transcript (WP-16) on a recorded backend: the real component and live renderer over a 600-line recording
    // that answers the corrections as WP-15 documents. No session, no network. Dev only.
    path: 'preview/flow-ended-transcript',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/meetings/preview/flow-ended-transcript-preview.component').then(
        (m) => m.FlowEndedTranscriptPreviewComponent,
      ),
    title: 'Flow ended transcript preview',
    data: { preview: true },
  },
  {
    // The live session's microphone picker on the real capture (no session, nothing sent): the fake-device specs'
    // harness (lane flow-mic-1015). Dev only.
    path: 'preview/flow-mic',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/live/preview/flow-mic-preview.component').then((m) => m.FlowMicPreviewComponent),
    title: 'Flow microphone picker preview',
    data: { preview: true },
  },
  {
    // WP-09: the recording pill's glyphs and states, "Where Flow listens" and "Someone is already recording" on scripted
    // recorders and a scripted capture graph (`?others=yuki,kenji&sameroom=kenji&source=laptop-sound`). Dev only.
    path: 'preview/flow-listen',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/meetings/session/where-flow-listens/preview/flow-listen-preview.component').then((m) => m.FlowListenPreviewComponent),
    title: 'Where Flow listens preview',
    data: { preview: true },
  },
  {
    // The real capture, upload and socket on wire v2 with a stub WebSocket: the two-channel Playwright check (lane
    // flow-v1-capture, WP-06). Dev only; the component provides its own services and `flow.multi-stream`.
    path: 'preview/flow-wire',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flow-v2/live/preview/flow-wire-preview.component').then((m) => m.FlowWirePreviewComponent),
    title: 'Flow wire v2 preview',
    data: { preview: true },
  },
  {
    // The shell's floating Flow recording widget in every state (`?state=`),
    // over a stand-in page, with stand-in controls: no session, nothing
    // recorded. The real orchestrator behind it: `/preview/flow-live?away=1`.
    path: 'preview/flow-recording-widget',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/shell/components/flow-recording-widget/flow-recording-widget-preview.component').then(
        (m) => m.FlowRecordingWidgetPreviewComponent,
      ),
    title: 'Flow recording widget preview',
    data: { preview: true },
  },
  {
    // The live session that follows you (Flow direction A): the top bar's session indicator, its live card
    // and the recording widget's takeover, over Flow's real pages on the sessions harness's in-memory API
    // (`day`, `m/<id>`, `boards/<id>`). No session; nothing is recorded or sent. Dev only.
    path: 'preview/flow-session-indicator',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/shell/components/flow-session-indicator/preview/flow-session-indicator-preview.routes').then(
        (m) => m.FLOW_SESSION_INDICATOR_PREVIEW_ROUTES,
      ),
    title: 'Flow session indicator preview',
    data: { preview: true },
  },
  {
    // Flow's shell with every section on synthetic data, no session: Home,
    // Recordings, Pages, Projects and a project, with Ask answering from a
    // stand-in. For watching and measuring the shell's motion. Dev only.
    path: 'preview/flow-shell',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/shell-motion/flow-shell-preview.routes').then((m) => m.FLOW_SHELL_PREVIEW_ROUTES),
    title: 'Flow shell preview',
    data: { preview: true },
  },
  {
    // The Flow Brain lab on synthetic data, signed out. The signed-in address is /flow/brain-lab. Dev only.
    path: 'preview/flow-brain',
    canActivate: [previewOnlyGuard],
    loadComponent: () => import('./features/flow-v2/brain/view/brain-lab.component').then((m) => m.BrainLabComponent),
    title: 'Flow Brain preview',
    data: { preview: true },
  },
  {
    // The end of Flow's capture journey with no session: the recording reader
    // on a synthetic recording whose summary can "arrive" (`?arrive=1`).
    // Nothing records or reaches the API. Dev only.
    path: 'preview/flow-capture',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/live/preview/flow-capture-preview.routes').then((m) => m.FLOW_CAPTURE_PREVIEW_ROUTES),
    title: 'Flow capture preview',
    data: { preview: true },
  },
  {
    // Flow meetings (P3) in Flow's shell with no session: a meeting before, during and after it
    // (`meeting?state=&role=`), the planner with Ask (`meetings/new?brief=`) and Flow's welcome. Synthetic
    // data, stand-in API, gemba and Flow Ask; nothing is sent or recorded. Dev only.
    path: 'preview/flow-meetings',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/meetings/preview/flow-meetings-preview.routes').then((m) => m.FLOW_MEETINGS_PREVIEW_ROUTES),
    title: 'Flow meetings preview',
    data: { preview: true },
  },
  {
    // Flow sessions and boards (the flow-sessions lane) in Flow's shell with no session: Home's "Start a
    // session", Sessions, a session's board, Boards with where each came from, and an old board's "Used in".
    // Synthetic data and a stand-in API; nothing is sent or recorded. Dev only.
    path: 'preview/flow-sessions',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/sessions/preview/flow-sessions-preview.routes').then((m) => m.FLOW_SESSIONS_PREVIEW_ROUTES),
    title: 'Flow sessions preview',
    data: { preview: true },
  },
  {
    // Flow direction B in Flow's shell with no session: Projects with New project, a project page as the memory
    // (since you were last here, timeline, position, needs you, people, live now), its invite dialog and people page,
    // the join link in each state, a session's outcomes to confirm, Add to project, and Home's "affects your work".
    // Synthetic data and a stand-in API; nothing is sent or recorded. Dev only.
    path: 'preview/flow-projects',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/projects/preview/flow-projects-preview.routes').then((m) => m.FLOW_PROJECTS_PREVIEW_ROUTES),
    title: 'Flow projects preview',
    data: { preview: true },
  },
  {
    // Flow's Open fold mark with no session: every state, transition and
    // variant (`''`), and Flow Home in Flow's shell on synthetic data
    // (`home`). Nothing records or reaches the API. Dev only.
    path: 'preview/flow-identity',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/identity/flow-identity-preview.routes').then((m) => m.FLOW_IDENTITY_PREVIEW_ROUTES),
    title: 'Flow identity preview',
    data: { preview: true },
  },
  {
    // Flow days (P9.01) in Flow's shell with no session: the day planner (`new`) and a day's page
    // (`day?state=draft|scheduled|clash`, `&rooms=on`, `&viewer=participant`). Synthetic data and a stand-in API;
    // nothing is sent, booked or recorded. Dev only.
    path: 'preview/flow-days',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/days/preview/flow-days-preview.routes').then((m) => m.FLOW_DAYS_PREVIEW_ROUTES),
    title: 'Flow days preview',
    data: { preview: true },
  },
  {
    // Flow Insights (the flow-insights lane) in Flow's shell with no session: Your Flow, Your meetings and Team on
    // synthetic data, the setup checklist and Share. `?empty=1`, `?team=none`, `?legacy=1`, `?partial=words` and
    // `?theme=`. A stand-in GraphQL client; nothing reaches the API. Dev only.
    path: 'preview/flow-insights',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/insights/preview/flow-insights-preview.routes').then((m) => m.FLOW_INSIGHTS_PREVIEW_ROUTES),
    title: 'Flow insights preview',
    data: { preview: true },
  },
  {
    // The Flowboard on a local document (C1: right-click menus, "+" handles, media; DIRECTION §6): the real board
    // page and canvas with no collaboration service, seeded with a sample board. `?viewer=1` opens it view-only,
    // `?ai=1` turns Ask's AI on. Nothing reaches the API. Dev only.
    path: 'preview/flow-board',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/flow-v2/boards/menus/preview/flow-board-preview.routes').then((m) => m.FLOW_BOARD_PREVIEW_ROUTES),
    title: 'Flowboard preview',
    data: { preview: true },
  },
  {
    // RETIRED (articles-v2, 10 Oct 2026): the legacy TipTap creator. Nothing
    // linked here any more, but the URL still served it, and it stored every
    // cover as a `data:` URI (`reader.readAsDataURL`), which the API now
    // refuses. One writer: `/write`, keeping the query.
    // `ArticleCreatorComponent` stays on disk for its two gate specs.
    path: 'home/create-article',
    redirectTo: toWriter,
  },
  {
    path: 'ai',
    loadComponent: () =>
      import('./features/ai-chat/ai-chat-page.component').then(
        (m) => m.AiChatPageComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('ai')],
    title: 'Workplace AI',
  },
  {
    // Public colleague profile — identity, achievement points, badge
    // showcase, visible contributions. Badge/contribution sections are
    // capability-gated inside the page (no route flag: the identity card
    // is always meaningful).
    path: 'people/:id',
    loadComponent: () =>
      import('./features/people/public-profile-page.component').then(
        (m) => m.PublicProfilePageComponent,
      ),
    canActivate: [authGuard],
    title: 'Profile',
  },
  {
    // Games Centre (Arcade) — SPEC-games-centre.md. Lazy route group;
    // capability-gated on `games` (default OFF per tenant). The navigation
    // registry entry, the AppSection union member, and this route land together
    // so nav visibility and deep-link gating stay in lockstep.
    path: 'games',
    loadChildren: () =>
      import('./features/games/games.routes').then((m) => m.GAMES_ROUTES),
    canActivate: [authGuard, featureFlagGuard('games')],
    title: 'Games',
    // Never preloaded at app start (`IdlePreloadStrategy`): most visits never
    // open a game, and the hub's cards prefetch each game on intent instead.
    data: { preload: false },
  },
  {
    // Content Centre — the editorial mini-app, same shape as the Games Centre
    // above. Lazy route group, capability-gated on `content.creation`. The
    // navigation registry entry, the AppSection union member, and this route
    // land together so nav visibility and deep-link gating stay in lockstep.
    //
    // The PERSON-level gate (`contentCentreGuard`) is inside CONTENT_ROUTES
    // rather than here, next to the DI provider it shares a scope with; the
    // nav entry mirrors it through `actionKeys`. `/preview/content` is the
    // unauthenticated fixture harness and is mounted separately below.
    path: 'content',
    loadChildren: () =>
      import('./features/content/content.routes').then((m) => m.CONTENT_ROUTES),
    canActivate: [
      authGuard,
      featureFlagGuard('content.creation'),
      trialFlagGuard(CONTENT_STUDIO_WORKSPACE_FLAG),
    ],
    // SHADOWED TODAY AND CORRECT ANYWAY. All five Studio children declare a
    // title and `DefaultTitleStrategy` keeps the deepest, so this string
    // reaches a tab only if a child is added without one — which is exactly
    // the moment the fallback needs to name the product rather than the URL
    // segment. `content-studio-naming.spec.ts` asserts it equals the EN
    // dictionary's `shell.sections.content`.
    title: 'Content Studio',
  },
  {
    // LIVE EVENTS — a workspace, not an admin page. See
    // `live-events.routes.ts` for why it moved out of `/admin`: the person
    // most likely to need it holds `live-event:broadcast` and nothing else,
    // and `adminGuard` matches no broadcast right, so they were bounced out
    // of the admin shell before the route was read.
    // The parent carries `authGuard` as well, even though both children
    // already do. Angular runs the guards on the matched route, so today the
    // leaves are covered and this adds nothing — it is here so that a child
    // added later without its own guard cannot be reached unauthenticated.
    // Same shape as `schedule`, `settings` and `events`.
    path: 'live-events',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/live-events/live-events.routes').then(
        (m) => m.LIVE_EVENTS_ROUTES,
      ),
    title: 'Live events',
  },
  {
    // The media library — one destination replacing two doors onto the same
    // collection: `/admin/media` (brand assets, behind adminGuard) and
    // `/content/library` (the writer's stock shelf). Both now redirect here.
    // Gates live in MEDIA_ROUTES; see its header for why neither adminGuard
    // nor a capability flag is one of them.
    path: 'media',
    loadChildren: () =>
      import('./features/media/media.routes').then((m) => m.MEDIA_ROUTES),
    title: 'Media library',
  },
  {
    // WHAT IS ON — every event across the organisation, the third view of the
    // Hubs workspace (Stream, Events). Guards are `/hubs`' exactly: the same
    // surface, the same any-of pair while `content.hubs` reaches every
    // catalogue.
    //
    // ⚠ DECLARED BEFORE `events/new` AND `events/:eventId`. Angular matches the
    // first route that fits, and a bare `/events` fits neither, but the order
    // is what keeps the next person from putting a param route above it.
    path: 'events',
    loadComponent: () =>
      import('./features/events/events-page.component').then(
        (m) => m.EventsPageComponent,
      ),
    canActivate: [
      authGuard,
      warmChunk(() => import('./features/events/events-page.component')),
      featureFlagAnyGuard(['content.hubs', 'content.discover']),
    ],
    title: 'Events',
  },
  {
    // Guard-free design/e2e harness for the page above. Every read behind it
    // is viewer-scoped and 401s unauthenticated, so the providers serve
    // fixtures through the real service's paging, filters and optimistic
    // replies. `?empty=1` and `?slow=1` show the empty and loading states.
    path: 'preview/events',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/events/events-page.component').then(
        (m) => m.EventsPageComponent,
      ),
    providers: [
      ORG_EVENTS_PREVIEW_PROVIDER,
      HUB_EVENTS_PREVIEW_PROVIDER,
      HUB_DIRECTORY_PREVIEW_PROVIDER,
      HUBS_PREVIEW_VIEWER_PROVIDER,
    ],
    title: 'Events (preview)',
    data: { preview: true },
  },
  {
    // Schedule a WorkplaceEvent. Gated on event:create (a default user
    // action) so a community organiser can create a listing without
    // holding a live-event broadcast grant. `/live-events/new` stays
    // the encoder-console path and still requires broadcast rights.
    path: 'events/new',
    loadComponent: () =>
      import('./features/live-events/live-event-setup.component').then(
        (m) => m.LiveEventSetupComponent,
      ),
    canActivate: [
      authGuard,
      anyActionGuard([
        'event:create',
        'live-event:manage',
        'live-event:broadcast',
      ]),
    ],
    title: 'Schedule an event',
  },
  {
    // THE EVENT PEOPLE OPEN (ADR-027). WorkplaceEvent is the event;
    // `/live-events` is the broadcaster console for the stream it may own.
    path: 'events/:eventId',
    loadComponent: () =>
      import('./features/events/event-page.component').then(
        (m) => m.EventPageComponent,
      ),
    canActivate: [authGuard],
    title: 'Event',
  },
  {
    path: 'map',
    loadComponent: () =>
      import('./features/viewer/viewer').then((m) => m.ViewerComponent),
    canActivate: [authGuard, featureFlagGuard('maps.wayfinding')],
    title: 'Map',
    // Marks THIS (employee) map as the one MapReuseStrategy keeps warm across
    // navigations. The admin estate map (/admin/map) also has leaf path 'map';
    // keying the reuse strategy on this flag — not the ambiguous path — stops
    // the two routes from sharing a cached component instance.
    data: { keepWarmMap: true },
  },
  {
    // Campus is now a mode of the unified /map page rather than a standalone
    // route. Redirect `/campus/:venueGroupId` into `/map?campus=<vgId>` —
    // ViewerComponent reads the `campus` query param after boot and enters
    // campus mode (source: 'deeplink'). Other query params are preserved.
    // CampusMapComponent is kept in the codebase for now (legacy standalone).
    path: 'campus/:venueGroupId',
    pathMatch: 'full',
    redirectTo: (r) =>
      inject(Router).createUrlTree(['/map'], {
        queryParams: { ...r.queryParams, campus: r.params['venueGroupId'] },
      }),
  },
  {
    // Previous-generation schedule. It held `/schedule` until the flip above;
    // it is still the only surface that serves desks, parking and zones, so
    // this is a MOVE and not a retirement — `scheduleLegacyRedirectGuard`
    // forwards both the legacy-shaped URLs and the room-less tenants here.
    //
    // Its `?type=` / `?tab=` tab switches now write onto this path, which is
    // why the guard no longer has to distinguish an in-app navigation from a
    // deep link: the page's own state never lands on `/schedule` any more.
    //
    // ANY-OF gate: reachable while at least one of its sub-view capabilities
    // is on; ScheduleComponent hides the gated sub-views and sanitises
    // `?type=` / `?tab=` deep links.
    path: 'schedule-legacy',
    loadComponent: () =>
      import('./features/schedule/schedule').then((m) => m.ScheduleComponent),
    canActivate: [
      authGuard,
      featureFlagAnyGuard([
        'rooms.booking',
        'desks.booking',
        'parking.booking',
        'schedule.events',
      ]),
    ],
    title: 'Schedule',
  },
  {
    path: 'visitors',
    loadComponent: () =>
      import('./features/visitors/visitors.component').then(
        (m) => m.VisitorsComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('visitors')],
    title: 'Visitors',
  },
  {
    // Declared as a sibling, not a child, so `/visitors` keeps its own
    // full-page component and this one replaces it rather than nesting.
    path: 'visitors/invite',
    loadComponent: () =>
      import('./features/visitors/invite-visitor.component').then(
        (m) => m.InviteVisitorComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('visitors')],
    title: 'Invite a visitor',
  },
  {
    path: 'insights',
    loadComponent: () =>
      import('./features/insights/insights-shell.component').then(
        (m) => m.InsightsShellComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('occupancy.insights')],
    title: 'Insights',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/insights/insights.component').then(
            (m) => m.InsightsComponent,
          ),
        title: 'Insights · Overview',
      },
      {
        path: 'catering',
        loadComponent: () =>
          import('./features/insights/catering/catering-insights.component').then(
            (m) => m.CateringInsightsComponent,
          ),
        title: 'Insights · Catering',
      },
    ],
  },
  {
    path: 'world',
    loadComponent: () =>
      import('./features/world-mode/world-mode.component').then(
        (m) => m.WorldModeComponent,
      ),
    canActivate: [authGuard, featureFlagGuard('world'), trialFlagGuard(WORLD_WORKSPACE_FLAG)],
    title: 'World',
  },
  {
    // `/m/<code>` is ONE door for two kinds of code (P7.04): a hosted
    // meeting's and a Flowboard invite's (P10 §4.5). The door has NO guard —
    // a guest has no session — and asks the API which kind the code is
    // (resolveFlowJoinCode): a meeting renders the join page right here; a
    // board (or an anonymous visitor, who can only be holding a board link)
    // goes on, replacing the URL, to `/m/<code>/board` below, which keeps
    // P4's exact guards and page, so a board link still signs people in and
    // brings them back. Outside the shell: a door, not a workspace page.
    // Both codes are redacted from every analytics URL (redact-join-code).
    path: 'm/:code/board',
    // Follows the entitlement (Flow capability): Flow on, or a live board code, which is an invitation. A session link
    // or a meeting code that lands here is sent on to the door at /m/<code>.
    canActivate: [authGuard, flowBoardCodeGuard],
    loadComponent: () => import('./features/flow-v2/boards/pages/join/flow-board-join.component').then((m) => m.FlowBoardJoinComponent),
    title: 'Join a board',
  },
  {
    // The door again, signed in. An anonymous visitor whose code is not a meeting's goes here to sign in
    // (authGuard) and comes back to ask the API what the code is: a board, or a Flow session's share link.
    path: 'm/:code/in',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/flow-meetings/join/flow-join-dispatcher.component').then((m) => m.FlowJoinDispatcherComponent),
    title: 'Join',
  },
  {
    path: 'm/:code',
    loadComponent: () =>
      import('./features/flow-meetings/join/flow-join-dispatcher.component').then((m) => m.FlowJoinDispatcherComponent),
    title: 'Join a meeting',
  },
  {
    // Flow — one shell (features/flow-v2/flow-shell.component) around every
    // Flow screen, declared in features/flow-v2/flow.routes.ts. The old Flow
    // shell and the classic presentations were removed on 8 October 2026; the
    // route table there redirects their old deep links.
    // Capability-gated: the whole Flow surface (incl. /flow/pages and the
    // legacy /projects + /knowledge redirects that land here) hides behind
    // the `flow` catalogue key.
    //
    // Flow off for the person: the "Flow isn't switched on for you" page (`/flow-off`), not a silent redirect to Home.
    // The exception is one session's own screens for a person INVITED to it (`flowAccessGuard`, which asks the API's
    // `myFlowSessionAccess`). It runs as canActivateChild too: moving between children does not re-run the parent.
    path: 'flow',
    canActivate: [authGuard, flowAccessGuard],
    canActivateChild: [flowAccessGuard],
    title: 'Flow',
    children: FLOW_V2_ROUTES,
  },
  {
    // "Flow isn't switched on for you" (the Flow capability, FC-6). A person who has Flow is sent to Flow.
    path: 'flow-off',
    canActivate: [authGuard, flowOffPageGuard],
    loadComponent: () => import('./features/flow-v2/access/flow-off.page').then((m) => m.FlowOffPageComponent),
    title: 'Flow is not switched on',
  },
  // Legacy `/projects` URLs redirect to the new /flow surface so
  // existing bookmarks + intra-app links keep working for one release.
  { path: 'projects', redirectTo: 'flow', pathMatch: 'full' as const },
  {
    path: 'projects/:id',
    redirectTo: 'flow/projects/:id',
    pathMatch: 'full' as const,
  },
  // Phase 6A — legacy top-level `/knowledge*` URLs redirect to the new
  // /flow/knowledge surface. Mirrors the /home/knowledge* redirects
  // placed above the /home parent.
  {
    path: 'knowledge',
    redirectTo: 'flow/knowledge',
    pathMatch: 'full' as const,
  },
  {
    path: 'knowledge/:workspaceId',
    redirectTo: 'flow/knowledge/:workspaceId',
    pathMatch: 'full' as const,
  },
  {
    path: 'knowledge/:workspaceId/:pageId',
    redirectTo: 'flow/knowledge/:workspaceId/:pageId',
    pathMatch: 'full' as const,
  },
  {
    path: 'knowledge/:workspaceId/:pageId/form',
    redirectTo: 'flow/knowledge/:workspaceId/:pageId/form',
    pathMatch: 'full' as const,
  },
  {
    // Settings. The path stays `/profile` — `/settings` is the admin shell
    // behind `adminGuard` — so every bookmark, push deep link and in-app
    // `/profile/...` navigation keeps working.
    path: 'profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/profile/settings/settings-area.component').then(
        (m) => m.SettingsAreaComponent,
      ),
    title: 'Settings',
    children: [
      ...settingsGenericChildren,

      // The pages the model marks `bespoke` — they draw content the row
      // model cannot describe (a vehicle list, a server-driven notification
      // matrix, two directions of delegation) and keep their own components.
      {
        path: 'vehicles',
        loadComponent: () =>
          import('./features/profile/settings/pages/vehicles-page.component').then(
            (m) => m.VehiclesPageComponent,
          ),
        title: 'Vehicles',
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/profile/settings/pages/notifications-page.component').then(
            (m) => m.NotificationsPageComponent,
          ),
        title: 'Notifications',
      },
      {
        path: 'delegation',
        loadComponent: () =>
          import('./features/profile/settings/pages/delegation-page.component').then(
            (m) => m.DelegationPageComponent,
          ),
        title: 'Delegation',
      },
      {
        path: 'access-tokens',
        loadComponent: () =>
          import('./features/profile/pages/access-tokens/profile-access-tokens-page.component').then(
            (m) => m.ProfileAccessTokensPageComponent,
          ),
        title: 'Access tokens',
      },
      {
        path: 'my-feedback',
        loadComponent: () =>
          import('./features/profile/pages/my-feedback/profile-my-feedback-page.component').then(
            (m) => m.ProfileMyFeedbackPageComponent,
          ),
        title: 'Your feedback',
      },
      {
        // Flow voice profiles (ADR-036 decision 9): set up, re-record and
        // delete the voice Flow names you by. The page itself says when the
        // organisation does not offer it.
        path: 'voice',
        loadComponent: () =>
          import('./features/profile/settings/pages/voice-page.component').then(
            (m) => m.SettingsVoicePageComponent,
          ),
        title: 'Your voice',
      },

      ...settingsRetiredChildren,
    ],
  },
  // The old design-system gallery's URLs (/design-system,
  // /settings/component-gallery, /admin/component-gallery). The gallery was
  // removed on 8 Oct 2026; Ricoh Paper's own docs site replaces it, so each old
  // link leaves for that site. No `authGuard`: the site is public. Declared
  // here, ahead of the `settings` and `admin` parents, so those parents' guards
  // never answer first. `core/guards/paper-docs-redirect.ts`.
  ...RETIRED_GALLERY_PATHS.map((path) => ({
    path,
    pathMatch: 'full' as const,
    canActivate: [toPaperDocs],
    children: [],
  })),
  {
    // Where the sign-in cycle stops. PUBLIC by necessity: `authGuard` sends the
    // user here precisely because no session could be established, so guarding
    // it would bounce them back into the loop it exists to end. The component
    // makes no backend call — see features/auth/sign-in-problem.
    path: 'sign-in-problem',
    loadComponent: () =>
      import('./features/auth/sign-in-problem/sign-in-problem.component').then(
        (m) => m.SignInProblemComponent,
      ),
    title: 'Sign-in problem',
  },
  {
    // Desktop only: sign-in happens in the system browser and comes back on
    // the ricohworkplace:// scheme. PUBLIC for the same reason as
    // sign-in-problem — the guard sends users here because there is no
    // session. Network-silent until the handoff lands (core/desktop-auth.ts).
    path: 'desktop-sign-in',
    loadComponent: () =>
      import('./features/desktop-sign-in/desktop-sign-in.component').then(
        (m) => m.DesktopSignInComponent,
      ),
    title: 'Sign in',
  },
  {
    // The desktop Flow recording widget's own window (src-tauri flow_desktop.rs).
    // PUBLIC and bare: it holds no session and makes no API call; it draws
    // what the main window sends it over Tauri events and sends presses back.
    // Only inside the desktop app: in a browser it would draw an idle Flow
    // capsule that does nothing, so the browser goes home (D15).
    path: 'desktop/flow-widget',
    canMatch: [() => isTauri || inject(Router).createUrlTree(['/'])],
    loadComponent: () =>
      import('./features/desktop-flow-widget/desktop-flow-widget.component').then(
        (m) => m.DesktopFlowWidgetComponent,
      ),
    title: 'Flow',
  },
  {
    // Signage — the in-app HTML signage player lib. Public (no auth): signage
    // runs on unattended displays ("content is just a URL"), screen identity
    // comes from pairing / the ScreenCloud SDK, and the preview hub is a public
    // gallery of its own. Lazy child route group; fully isolated
    // under features/signage so it can be lifted into a standalone app later.
    path: 'signage',
    loadChildren: () =>
      import('./features/signage/signage.routes').then((m) => m.SIGNAGE_ROUTES),
    title: 'Signage',
  },
  {
    // Outlook add-in task pane (unified manifest `extensions` runtime,
    // entered via /addin/taskpane.html which redirects here preserving the
    // Office bootstrap params). No auth guard — the pane runs its own
    // silent NAA + interactive sign-in via AddinAuthService, mirroring the
    // Teams tab SSO. Bare route: rendered without the employee shell.
    path: 'addin/rooms',
    loadComponent: () =>
      import('./features/addin/addin-rooms-page.component').then(
        (m) => m.AddinRoomsPageComponent,
      ),
    title: 'Find a room',
  },
  {
    // Outlook add-in preview — public harness rendering the real task
    // pane against a mock window.Office + fixture data (no Outlook, no
    // network). Scenarios: compose / read / signed-out / outside-Office.
    path: 'preview/addin-rooms',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/addin/addin-rooms-preview-page.component').then(
        (m) => m.AddinRoomsPreviewPageComponent,
      ),
    title: 'Outlook add-in preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Schedule V2 — public preview harness for every new atom from
    // the Room Schedule 2 prototype. No auth guard so the Playwright
    // e2e suite can exercise each component in isolation against
    // mock data. Inside the app, navigate to /preview/schedule-v2.
    path: 'preview/schedule-v2',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/schedule-v2-preview/schedule-v2-preview-page.component').then(
        (m) => m.ScheduleV2PreviewPageComponent,
      ),
    title: 'Schedule V2 preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Games Centre — public preview harness. Same components as /games but
    // backed by the in-memory mock API (practice-style local seeds, no
    // network, no auth) so gameplay, the hub, boards and the challenge view
    // can be exercised by Playwright and visual review without a session.
    path: 'preview/games',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/games/games.routes').then(
        (m) => m.GAMES_PREVIEW_ROUTES,
      ),
    title: 'Games preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Public design/e2e harness for the Content Centre. Same components, mock
    // port, no auth — like its five siblings above. The authenticated mount is
    // `/content`, above, next to Games.
    //
    // It is not a convenience. The fixtures behind it are chosen to put every
    // state of the centre's honesty rules on one page: the six audience
    // verdicts, the four acknowledgement readings, an absent location list, a
    // bucket count nobody counted and an unrecognised status. So a reviewer
    // with no login and no seeded database can check by looking that an
    // unanswerable question is presented as unanswerable rather than as a
    // zero — which is the one property this surface exists to have.
    path: 'preview/content',
    canActivate: [previewOnlyGuard],
    loadChildren: () =>
      import('./features/content/content.routes').then(
        (m) => m.CONTENT_PREVIEW_ROUTES,
      ),
    title: 'Content preview',
    data: { preview: true },
  },
  {
    // The map's asset popup and asset card (asset cards lane, 5 Oct 2026):
    // the Rum Runner printer and coffee machine from fixtures mirroring the
    // live API, every read stubbed in the page. `?asset=` isolates one,
    // `?theme=` sets the paper. See e2e/asset-cards.spec.ts.
    path: 'preview/asset-cards',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/asset-cards-preview/asset-cards-preview.component').then(
        (m) => m.AssetCardsPreviewComponent,
      ),
    title: 'Asset cards preview',
    data: { preview: true },
  },
  {
    // Booking panel states — public preview harness rendering every
    // implemented space-detail-panel state from typed fixtures (T0.2 of
    // the booking panel overhaul). No auth guard; all panel data services
    // are stubbed at the component level so no network calls fire.
    // `?state=<fixture-id>` isolates one state for axe/visual runs —
    // see docs/PANEL_STATES_MATRIX.md (its e2e/panel-states.spec.ts was retired 8 Oct 2026).
    path: 'preview/panel-states',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/panel-states-preview/panel-states-preview-page.component').then(
        (m) => m.PanelStatesPreviewPageComponent,
      ),
    title: 'Panel states preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    path: 'preview/desk-slot-surfaces',
    canActivate: [previewOnlyGuard],
    loadComponent: () => import('./features/desk-slot-preview/desk-slot-surfaces-preview.component').then(m => m.DeskSlotSurfacesPreviewComponent),
    title: 'Isolated desk booking surfaces',
    data: { preview: true },
  },
  {
    path: 'preview/desk-slots',
    canActivate: [previewOnlyGuard],
    loadComponent: () => import('./features/desk-slot-preview/desk-slot-preview.component').then(m => m.DeskSlotPreviewComponent),
    title: 'Desk slot interaction preview',
    data: { preview: true },
  },
  {
    // The map page's flat floor plan (no WebGL, ?tier=none, a slow or failed map), over the committed demo floor with
    // fixtures for the platform and the panel. No auth, no network beyond the demo bundle.
    path: 'preview/flat-plan',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/flat-plan-preview/flat-plan-preview.component').then((m) => m.FlatPlanPreviewComponent),
    title: 'Flat floor plan preview',
    data: { preview: true },
  },
  {
    // Map waitlist banner — public preview harness rendering every state
    // of the floating fully-booked / on-list banner (im-waitlist-banner)
    // against typed fixtures. Presentational component — no auth, no
    // network, outputs log to an on-page event list.
    path: 'preview/waitlist-banner',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/waitlist-banner-preview/waitlist-banner-preview-page.component').then(
        (m) => m.WaitlistBannerPreviewPageComponent,
      ),
    title: 'Waitlist banner preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // A visit whose meeting changed without it (rdx-platform-api#972): the
    // real /visitors page and the meeting Visitors tab (`?surface=meeting`)
    // over an in-memory host-visits service. No auth, no network.
    path: 'preview/visit-meeting-change',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/visitors/preview/visit-meeting-change-preview.component').then(
        (m) => m.VisitMeetingChangePreviewComponent,
      ),
    title: 'Visit meeting change preview',
    data: { preview: true },
  },
  {
    // Giving a meeting's visitors a desk, parking or a room per day: the real
    // meeting visitors list and visit drawer over in-memory host-visit and
    // allocation services (`?open=v-group`, `?spaces=refuse|fail`). No auth.
    path: 'preview/visit-spaces',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/visitors/preview/visit-spaces-preview.component').then(
        (m) => m.VisitSpacesPreviewComponent,
      ),
    title: 'Visit spaces preview',
    data: { preview: true },
  },
  {
    // Community apps preview — public harness for the community detail
    // "Apps" tab (app store + step challenges + leaderboard) on a STATEFUL
    // in-memory mock: install / create / join / log / sync all work with no
    // auth and no network. `?member=1` = plain-member view; `?fresh=1` =
    // nothing installed (lands on the store).
    path: 'preview/community-apps',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/community-apps-preview/community-apps-preview-page.component').then(
        (m) => m.CommunityAppsPreviewPageComponent,
      ),
    title: 'Community apps preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The Services page's old harness. Services is a page of Home now, and its
    // harness is Home's live one on Services' sample (scenarios in
    // `services-workspace-sample.ts`), so this lands there with the query.
    path: 'preview/services-workspace',
    pathMatch: 'full',
    redirectTo: redirectKeepingQuery('/preview/home-workspace-live/services'),
    data: { preview: true },
  },
  {
    // Public Access preview — the real /admin/access surface on the
    // roles-access prototype's fixtures, with no auth and no network. Two
    // params drive what the design turns on:
    //   ?actor=global|retail  — the granter's reach (a Retail division admin
    //                           sees only their subtree, and no global option)
    //   ?estate=nested|flat   — a customer without sub-divisions never meets
    //                           the hierarchy model
    path: 'preview/access',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/access-preview/access-preview-page.component').then(
        (m) => m.AccessPreviewPageComponent,
      ),
    title: 'Access preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Public admin services editor preview — real editor on deterministic
    // providers, estate scopes and form fields; no auth or network.
    path: 'preview/admin-services/:id',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/services-preview/services-admin-preview-page.component').then(
        (m) => m.ServicesAdminPreviewPageComponent,
      ),
    title: 'Services admin preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Public map preview — renders a REAL ingested building floor with no
    // auth guard, so the Playwright map-QA harness can screenshot + assert
    // every ingested map. Live data still needs a session (the harness
    // establishes one via the authenticatedPage fixture). Params:
    //   :locationId — platform location _id
    //   :floorId    — optional platform floor _id (defaults to the building's
    //                 default floor when omitted).
    path: 'preview/map/:locationId/:floorId',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/viewer/preview-map.component').then(
        (m) => m.PreviewMapComponent,
      ),
    title: 'Map preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The turn-by-turn directions panel and the engine session over a sample
    // two-floor route, in any of the seven languages — no session, no backend,
    // no Mapbox. `previewOnlyGuard` keeps it out of production.
    path: 'preview/directions',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/directions-preview/directions-preview-page.component').then(
        (m) => m.DirectionsPreviewPageComponent,
      ),
    title: 'Directions preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The map search panel over a fixed floor and a fixed clock — no session,
    // no backend, no Mapbox. `previewOnlyGuard` keeps it out of production.
    path: 'preview/map-search',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/map-search-preview/map-search-preview-page.component').then(
        (m) => m.MapSearchPreviewPageComponent,
      ),
    title: 'Map search preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    path: 'preview/map/:locationId',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/viewer/preview-map.component').then(
        (m) => m.PreviewMapComponent,
      ),
    title: 'Map preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // Directions on the shipped demo floor — the real panel, routing and the
    // engine's building router over walkways drafted by its detector. No
    // auth, no GraphQL (docs/TURN_BY_TURN.md).
    path: 'preview/map-directions',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/directions-preview/map-directions-preview.component').then(
        (m) => m.MapDirectionsPreviewComponent,
      ),
    title: 'Map directions preview',
    data: { preview: true },
  },
  {
    // Visual-style A/B lab — the shipped demo floor rendered through the real
    // engine under the current default aesthetic vs the Intelligent Workplace
    // one derived from the Blender prototype's style guide. No auth, no
    // GraphQL: the bundle is assembled from `public/output/`.
    path: 'preview/map-style',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/map-style-lab/map-style-lab.component').then(
        (m) => m.MapStyleLabComponent,
      ),
    title: 'Map style lab',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The multi-day desk strip over the demo floor: the real strip, key,
    // coordinator and DeskDaysService over a fixture transport. No session,
    // no GraphQL. The switch-trace harness for indoor_mapping_poc-e1so.
    path: 'preview/desk-days',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/desk-days-preview/desk-days-preview-page.component').then(
        (m) => m.DeskDaysPreviewPageComponent,
      ),
    title: 'Multi-day desk preview',
    // Marked so `IdlePreloadStrategy` skips it — preloading a route
    // creates its injector and runs its environment initializers.
    data: { preview: true },
  },
  {
    // The map page in Louis's design language: a still mock over the demo
    // floor, for the proposal in docs/MAP_LOUIS_PROPOSAL.md. No session, no
    // GraphQL. `?state=` picks the element group, `?theme=` the paper.
    path: 'preview/map-louis',
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/map-louis-preview/map-louis-preview.component').then(
        (m) => m.MapLouisPreviewComponent,
      ),
    title: 'Map in Louis’s language',
    data: { preview: true },
  },
  {
    // Diagnostic: minimal standalone component to verify route matching
    // works at all in this build. If THIS renders but /preview/schedule-v2
    // doesn't, the issue is in the preview component's imports.
    path: 'sv2hello',
    // Guarded with the other harnesses. It is a diagnostic for
    // `/preview/schedule-v2` and belongs behind the same door — it only
    // escaped the sweep because it does not sit under the `preview/` prefix.
    canActivate: [previewOnlyGuard],
    loadComponent: () =>
      import('./features/schedule-v2-preview/sv2-hello.component').then(
        (m) => m.Sv2HelloComponent,
      ),
    title: 'Schedule V2 preview (diag)',
  },
  {
    // B11.1 — AdminShellComponent is the top-level layout wrapper for
    // every `/settings/*` URL. It owns the 240px rail column that the
    // brand block (B11.2), notifications surface (B11.4), and rail
    // topology change (B10.1) will land into. The existing
    // SettingsPageComponent (admin rail + per-page outlet) is mounted
    // as the default child so this slice is structural only — no
    // visible change. Every previously-declared settings child route
    // is moved one level deeper, nested under SettingsPageComponent so
    // it continues to render into the same `<router-outlet>` it always
    // has.
    path: 'settings',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/settings/admin-shell/admin-shell.component').then(
        (m) => m.AdminShellComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/settings/settings-page.component').then(
            (m) => m.SettingsPageComponent,
          ),
        children: [
          { path: '', redirectTo: 'divisions', pathMatch: 'full' as const },
          // Organisation moved to the new admin shell at /admin/organisation (the
          // single, fresh-look, ?tab=-driven Organisation surface). These redirects
          // keep old bookmarks / deep-links working, preserving query params and
          // mapping the legacy child segments onto the new ?tab= values.
          // `pathMatch: 'full'`.
          {
            path: 'organisation',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/organisation'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'organisation/calendar',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/organisation'], {
                queryParams: { ...r.queryParams, tab: 'calendar' },
              }),
          },
          {
            path: 'organisation/branding',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/organisation'], {
                queryParams: { ...r.queryParams, tab: 'branding' },
              }),
          },
          {
            // Policy approval settings were removed (October 2026): the
            // Approvals tab is gone, so old links land on Organisation.
            path: 'organisation/approvals',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/organisation'], {
                queryParams: r.queryParams,
              }),
          },
          {
            // Legacy org "Feature flags" segment — the new design embeds a real
            // Feature flags tab (same FeatureFlagsAdminService data), so land there.
            path: 'organisation/features',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/organisation'], {
                queryParams: { ...r.queryParams, tab: 'flags' },
              }),
          },
          // Divisions moved to the new admin shell at /admin/divisions (existing
          // list + detail components mounted as-is). Query-preserving redirects;
          // pathMatch 'full' so the bare `divisions` redirect doesn't swallow
          // `divisions/:id` (declared separately below).
          {
            path: 'divisions',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/divisions'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'apps',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/apps'], {
                queryParams: r.queryParams,
              }),
          },
          // Backwards-compat: the old full-page /divisions/new route was retired
          // when create moved into the side-panel pattern. Stray bookmarks land
          // back on the (now /admin) list instead of triggering an empty edit form.
          {
            path: 'divisions/new',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/divisions'], {
                queryParams: r.queryParams,
              }),
          },
          // Onboarding moved to the new admin shell at /admin/onboarding (+
          // /admin/onboarding/greenfield). Query-preserving redirects; pathMatch
          // 'full' so the bare `onboarding` redirect doesn't swallow the literal
          // `onboarding/greenfield` sibling.
          {
            path: 'onboarding',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/onboarding'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'onboarding/greenfield',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/onboarding/greenfield'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'divisions/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/divisions', r.params['id']],
                { queryParams: r.queryParams },
              ),
          },
          // Redirects to /profile for backwards compatibility
          {
            path: 'profile',
            redirectTo: '/profile/account',
            pathMatch: 'full' as const,
          },
          {
            path: 'preferences',
            redirectTo: '/profile/account',
            pathMatch: 'full' as const,
          },
          {
            // Habits and Focus was retired with the Settings rebuild. Two
            // hops (`/settings/habits` → `/profile/habits` → `/profile`)
            // would work, but only by accident of ordering — point it at the
            // surviving destination directly.
            path: 'habits',
            redirectTo: '/profile',
            pathMatch: 'full' as const,
          },
          {
            path: 'notifications',
            redirectTo: '/profile/notifications',
            pathMatch: 'full' as const,
          },
          // Integrations moved to the new admin shell at /admin/integrations (+
          // activity / asset-discovery / microsoft/live literals + the
          // `:provider` detail). Query/param-preserving redirects; pathMatch
          // 'full' so the bare `integrations` redirect doesn't swallow the
          // sub-paths. The three literals precede the `:provider` redirect.
          {
            path: 'integrations',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/integrations'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'integrations/activity',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/integrations/activity'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'integrations/asset-discovery',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/integrations/asset-discovery'],
                { queryParams: r.queryParams },
              ),
          },
          {
            path: 'integrations/microsoft/live',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/integrations/microsoft/live'],
                { queryParams: r.queryParams },
              ),
          },
          {
            path: 'integrations/:provider',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/integrations', r.params['provider']],
                { queryParams: r.queryParams },
              ),
          },
          // Locations moved to the new admin shell at /admin/locations (re-skinned
          // card list + hero/tabs detail). Query-preserving redirect; pathMatch
          // 'full' so it doesn't swallow locations/:id (declared separately below).
          {
            path: 'locations',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/locations'], {
                queryParams: r.queryParams,
              }),
          },
          // Building alerts moved to the new admin shell at /admin/alerts (the
          // featureFlagGuard now lives on the /admin route). Query-preserving
          // redirect.
          {
            path: 'alerts',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/alerts'], {
                queryParams: r.queryParams,
              }),
          },
          // Content management moved to the new admin shell at /admin/content
          // (the featureFlagGuard now lives on the /admin route). Query-preserving
          // redirect.
          {
            path: 'content',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/content'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'locations/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/locations', r.params['id']],
                { queryParams: r.queryParams },
              ),
          },
          // Campuses moved to the new admin shell at /admin/campuses (+ the
          // dirty-guarded `:id` detail). Query/param-preserving redirects;
          // pathMatch 'full' so the bare `campuses` redirect doesn't swallow
          // `campuses/:id`.
          {
            path: 'campuses',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/campuses'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'campuses/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/campuses', r.params['id']],
                { queryParams: r.queryParams },
              ),
          },
          // Spaces moved to the new admin shell at /admin/spaces (the single
          // Spaces admin surface). These redirects keep old bookmarks / deep
          // links working, preserving query params (saved views / URL-state) and
          // the :id. `pathMatch: 'full'` so the bare `spaces` redirect doesn't
          // swallow `spaces/sub-types` or `spaces/:id`.
          {
            path: 'spaces',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/spaces'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'spaces/sub-types',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/sub-types'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'spaces/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/spaces', r.params['id']], {
                queryParams: r.queryParams,
              }),
          },
          // Assets moved to the new admin shell at /admin/assets (+ the literal
          // `assets/new` create + the `:id` detail). Query/param-preserving
          // redirects; pathMatch 'full'. `assets/new` precedes `assets/:id` so
          // the literal wins.
          {
            path: 'assets',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/assets'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'assets/new',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/assets/new'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'assets/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/assets', r.params['id']], {
                queryParams: r.queryParams,
              }),
          },
          // Problem categories + Common problems live in the new admin shell
          // (they never had /settings pages); explicit redirects so the
          // settings-rail entries land on the right page instead of being
          // swallowed by the `**` safety net (which drops to /admin overview).
          {
            path: 'problem-categories',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/problem-categories'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'common-problems',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/common-problems'], {
                queryParams: r.queryParams,
              }),
          },
          // Screens admin area (list + templates + :id detail) — same deal.
          // Literal `screens/templates` precedes `screens/:id`.
          {
            path: 'screens',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/screens'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'screens/templates',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/screens/templates'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'screens/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/screens', r.params['id']], {
                queryParams: r.queryParams,
              }),
          },
          // ── Access & audit (flat routes — surfaced as individual
          //    main-rail entries, no sub-shell). Roles / People /
          //    Effective permissions / Features each render full-width.
          //    Old /settings/access entry redirects to Roles so existing
          //    bookmarks survive.
          {
            path: 'access',
            redirectTo: 'access/roles',
            pathMatch: 'full' as const,
          },
          // Roles moved to the new admin shell at /admin/roles (fresh-look list +
          // re-skinned detail). These redirects keep old bookmarks / deep-links
          // working, preserving query params + the :id. `pathMatch: 'full'`.
          {
            path: 'access/roles',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/roles'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'access/roles/:id',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/roles', r.params['id']], {
                queryParams: r.queryParams,
              }),
          },
          // People moved to the new admin shell at /admin/people (fresh-look
          // directory). Query-preserving redirect.
          {
            path: 'access/people',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/people'], {
                queryParams: r.queryParams,
              }),
          },
          // Effective permissions + Features moved to the new admin shell at
          // /admin/permissions + /admin/features. Query-preserving redirects.
          {
            path: 'access/permissions',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/permissions'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'access/features',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/features'], {
                queryParams: r.queryParams,
              }),
          },
          // Backwards-compat: the original /settings/permissions URL now
          // redirects straight to the new /admin/permissions home so any
          // existing bookmarks survive.
          {
            path: 'permissions',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/permissions'], {
                queryParams: r.queryParams,
              }),
          },
          // Policies + Proposals moved to the new admin shell at
          // /admin/policies + /admin/proposals (the single Policies admin
          // surface). These redirects keep old bookmarks / deep-links working,
          // preserving query params (?tab=, ?mode=editor, ?policyId=, ?draftId=
          // …) and the legacy child paths. `pathMatch: 'full'` so each matches
          // its exact path. The policyManagementGuard + policyTierRedirectGuard
          // now live on the /admin routes (see admin.routes.ts), so an
          // unauthorised deep-link still hard-404s after the hop.
          {
            path: 'policies',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'policies/quick',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies/quick'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'policies/standard',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies/standard'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'policies/advanced',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies/advanced'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'policies/proposals',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'proposals',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/policies'], {
                queryParams: r.queryParams,
              }),
          },
          // Activity Console moved to the new admin shell at /admin/activity.
          // Query-preserving redirect.
          {
            path: 'activity',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/activity'], {
                queryParams: r.queryParams,
              }),
          },
          // Catering Admin moved to the new admin shell at /admin/catering (the
          // featureFlagGuard + the '' → vendors default now live on the /admin
          // subtree). The legacy nested block is replaced with flat query/param-
          // preserving redirects, one per former child path.
          {
            path: 'catering',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/catering'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'catering/vendors',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/catering/vendors'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'catering/vendors/:vendorId',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/catering/vendors', r.params['vendorId']],
                { queryParams: r.queryParams },
              ),
          },
          {
            path: 'catering/menus/:vendorId',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(
                ['/admin/catering/menus', r.params['vendorId']],
                { queryParams: r.queryParams },
              ),
          },
          {
            path: 'catering/orders',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/catering/orders'], {
                queryParams: r.queryParams,
              }),
          },
          // Brand Test moved to the new admin shell at /admin/brand-test.
          {
            path: 'brand-test',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/brand-test'], {
                queryParams: r.queryParams,
              }),
          },
          // Map Ingest (+ the literal `map-ingest/upload` wizard) moved to the
          // new admin shell at /admin/map-ingest. Query-preserving redirects;
          // pathMatch 'full' so the bare `map-ingest` redirect doesn't swallow
          // `map-ingest/upload`.
          {
            path: 'map-ingest',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/map-ingest'], {
                queryParams: r.queryParams,
              }),
          },
          {
            path: 'map-ingest/upload',
            pathMatch: 'full',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin/map-ingest/upload'], {
                queryParams: r.queryParams,
              }),
          },
          // Safety net for the /settings → /admin migration: any settings deep
          // link or external bookmark that wasn't given an explicit redirect
          // above lands on the admin shell instead of falling through to the
          // top-level `**` → /home (which silently dropped admins on Home). Must
          // be LAST so it never shadows a specific redirect.
          {
            path: '**',
            redirectTo: (r) =>
              inject(Router).createUrlTree(['/admin'], {
                queryParams: r.queryParams,
              }),
          },
        ],
      },
    ],
  },
  {
    // Catering is ordered in Home's Services now, in its right-hand panel (Nath, 4 Oct: "bring the order catering
    // into the same flow"). The old page's address lands there with the catering order open and its query kept
    // (`?meeting=<booking id>` or `?space=<id>` choose what it opens on).
    path: 'catering/order',
    pathMatch: 'full',
    redirectTo: toCateringOrder,
  },
  {
    // The catering emails' links (rdx-platform-api, `notifications/templates/email/mjml/seeds/catering.ts`) name
    // paths no employee page answered, so they fell to the wildcard. The cut-off warning's `/catering` asks for an
    // order: Services with the order open. The order emails' `/catering/orders`: My requests, where it is listed.
    path: 'catering',
    pathMatch: 'full',
    redirectTo: toCateringOrder,
  },
  {
    path: 'catering/orders',
    pathMatch: 'full',
    redirectTo: toCateringOrders,
  },
  {
    // The catering approval email's old link. Approvers act in Manage (Nath, 5 Oct: "For catering approvers it should
    // go to the manage application"), and this app has no approvals page, so the browser goes on to Manage's
    // Requests board with the query. No `authGuard`: Manage signs the approver in itself. `core/guards/manage-redirect.ts`.
    path: 'catering/approvals',
    pathMatch: 'full',
    canActivate: [toManageCateringApprovals],
    children: [],
  },
  {
    path: 'catering/vendors/:vendorId/menu/:menuId',
    loadComponent: () =>
      import('./features/settings/pages/catering/catering-menu-editor-page.component').then(
        (m) => m.CateringMenuEditorPageComponent,
      ),
    canActivate: [authGuard],
    title: 'Menu Manager',
  },
  {
    path: 'vendor-portal',
    loadComponent: () =>
      import('./features/settings/pages/catering/vendor-portal-page.component').then(
        (m) => m.VendorPortalPageComponent,
      ),
    canActivate: [authGuard],
    title: 'Vendor Portal',
  },
  {
    path: 'vendor-portal/menus/:menuId',
    loadComponent: () =>
      import('./features/settings/pages/catering/catering-menu-editor-page.component').then(
        (m) => m.CateringMenuEditorPageComponent,
      ),
    canActivate: [authGuard],
    data: { editorMode: 'vendor' },
    title: 'Menu Manager — Vendor Portal',
  },
  // ── Vendor Portal (standalone, no main app chrome) ────────
  {
    path: 'vendor/sign-in',
    loadComponent: () =>
      import('./features/vendor-portal/vendor-sign-in.component').then(
        (m) => m.VendorSignInComponent,
      ),
    title: 'Sign In — Vendor Portal',
  },
  {
    path: 'vendor',
    loadComponent: () =>
      import('./features/vendor-portal/vendor-portal-shell.component').then(
        (m) => m.VendorPortalShellComponent,
      ),
    canActivate: [vendorGuard],
    title: 'Vendor Portal',
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' as const },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/vendor-portal/vendor-dashboard.component').then(
            (m) => m.VendorDashboardComponent,
          ),
        title: 'Dashboard — Vendor Portal',
      },
      {
        path: 'menus',
        loadComponent: () =>
          import('./features/vendor-portal/vendor-menus.component').then(
            (m) => m.VendorMenusComponent,
          ),
        title: 'Menus — Vendor Portal',
      },
      {
        path: 'menus/:menuId',
        loadComponent: () =>
          import('./features/settings/pages/catering/catering-menu-editor-page.component').then(
            (m) => m.CateringMenuEditorPageComponent,
          ),
        data: { editorMode: 'vendor' },
        title: 'Menu Editor — Vendor Portal',
      },
      {
        path: 'orders',
        loadComponent: () =>
          import('./features/vendor-portal/vendor-orders.component').then(
            (m) => m.VendorOrdersComponent,
          ),
        title: 'Orders — Vendor Portal',
      },
      {
        path: 'team',
        loadComponent: () =>
          import('./features/vendor-portal/vendor-team.component').then(
            (m) => m.VendorTeamComponent,
          ),
        title: 'Team — Vendor Portal',
      },
    ],
  },
  // ── Admin shell (extractable lib) ─────────────────────────
  {
    // Public, auth-free gallery of the admin component kit (drives the WCAG
    // axe spec). Bare route — see app.ts isAdminRoute.
    path: 'admin-gallery',
    loadComponent: () =>
      import('./admin/gallery/admin-gallery-page.component').then(
        (m) => m.AdminGalleryPageComponent,
      ),
    title: 'Admin Kit — Gallery',
  },
  {
    // Full-bleed admin shell. Bare route — bypasses the employee glass-bar
    // chrome via app.ts isAdminRoute.
    path: 'admin',
    loadChildren: () =>
      import('./admin/admin.routes').then((m) => m.ADMIN_ROUTES),
    // adminRedirectGuard runs FIRST: when NG_APP_SETTINGS_APP_URL is set it
    // forwards to the migrated Workplace Settings app and cancels this route
    // (auth/admin guards never run). Unset = legacy admin, guards apply.
    canActivate: [adminRedirectGuard, authGuard, adminGuard],
  },
  {
    // Unknown URLs land on the default home surface (Louis's Home, at `/home`).
    path: '**',
    redirectTo: 'home',
  },
];
